/**
 * Material Calculator — Milestone 2 of the Product Catalog roadmap.
 *
 * Pick 1–4 named systems side-by-side, enter the roof's total SF, toggle
 * a waste factor + optional add-ons (walk-pads, primer/wash, etc.), and
 * the page computes:
 *
 *   1. Raw quantities per recipe line (`coverage_rate × sf / 100` for
 *      `per_100sf` basis), bumped by waste %.
 *   2. The optimal mix of WC container sizes (tote → drum → pail) to
 *      cover that quantity. Greedy: fill the biggest first, then the
 *      next size for remaining whole-container chunks, then the smallest
 *      for the final remainder.
 *   3. Raw subtotal, +markup, +handling = customer-facing price.
 *
 * Markup math (matches calculator_settings):
 *      cost      = sum(container_count × container_price)
 *      marked_up = cost × (1 + markup_pct/100)         # 15% default
 *      handling  = marked_up × (handling_pct/100)      # 10% default,
 *                                                       compounded on
 *                                                       marked-up total
 *      customer  = marked_up + handling
 *
 * Deal hand-off (Milestone 3): when the page is opened with
 * `?deal=<id>` (e.g. via the “Pull from Calculator” button on
 * Deal Detail’s Vendor Cost section), the chosen system’s BoM lines
 * are PUT back to that deal as `cost_items` with category="Materials".
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calculator as CalcIcon, Plus, X, Save, ChevronLeft, Layers, AlertCircle, Loader2, FileText, Download, PenLine, Package } from "lucide-react";
import { ScopeLaneSwitcher, RecentScopes } from "@/components/ScopeLaneSwitcher";

const ADDON_TEMPLATES = [
  // Walk-pads
  { id: "walk_pads_grey",   label: "Walk Pads (Grey)",         sku: "850 SWS Grey",   unit: "gal",   default_qty: 0 },
  { id: "walk_pads_yellow", label: "Walk Pads (Burnt Yellow)", sku: "850 SWS Yellow", unit: "gal",   default_qty: 0 },
  // Prep / sealants
  { id: "roof_wash",        label: "Roof Wash & Prep",         sku: "9000",           unit: "gal",   default_qty: 0 },
  // Spray Elastic Cement comes ONLY in drums (no smaller pack). Qty entered
  // in gallons, packed automatically into 55-gal drums by the calc engine.
  { id: "flash_cement_spray", label: "Spray Elastic Cement",   sku: "800 Spray",      unit: "gal",   default_qty: 0 },
  { id: "flash_cement_w",   label: "Elastic Cement White",     sku: "800 W",          unit: "gal",   default_qty: 0 },
  { id: "flash_cement_b",   label: "Elastic Cement Black",     sku: "801 B",          unit: "gal",   default_qty: 0 },
  // Reinforcing fabric strips (sold by the roll — qty here is # of rolls)
  { id: "fabric_4in",       label: 'Fabric 4" x 300\'',        sku: '4" x 300\'',     unit: "rolls", default_qty: 0 },
  { id: "fabric_6in",       label: 'Fabric 6" x 300\'',        sku: '6" x 300\'',     unit: "rolls", default_qty: 0 },
  { id: "fabric_12in",      label: 'Fabric 12" x 300\'',       sku: '12" x 300\'',    unit: "rolls", default_qty: 0 },
  { id: "fabric_20in",      label: 'Fabric 20" x 300\'',       sku: '20" x 300\'',    unit: "rolls", default_qty: 0 },
];

// Everest-specific add-ons (Silkoxy Patch / Flashing Grade, EcoLevel, EverStitch
// fabric strips, Silkoxy Ever-Tread Walk Pad) plus SESCO granules — only shown
// in the sidebar when the selected vendor is Everest Systems. Each item maps to
// a real product_catalog row by `match` (vendor + name contains). Granule rows
// trigger a flat $2,000 SESCO freight surcharge on the column (added once if
// any granule qty > 0).
const EVEREST_ADDON_TEMPLATES = [
  // EcoLevel (slope-correction kits)
  { id: "ev_ecolevel_2_5",  label: "EcoLevel — 2.5 Gal Kit",   match: { vendor: "Everest Systems", name_includes: "EcoLevel — 2.5 Gallon Kit" }, unit: "kit",  default_qty: 0 },
  { id: "ev_ecolevel_4",    label: "EcoLevel — 4 Gal Kit",     match: { vendor: "Everest Systems", name_includes: "EcoLevel — 4 Gallon Kit" },   unit: "kit",  default_qty: 0 },
  // EverStitch reinforcing fabric (per roll)
  { id: "ev_stitch_4",   label: 'EverStitch 272 — 4" x 300\'',  match: { vendor: "Everest Systems", name_includes: 'EverStitch 272 — 4"' },  unit: "roll", default_qty: 0 },
  { id: "ev_stitch_6",   label: 'EverStitch 272 — 6" x 300\'',  match: { vendor: "Everest Systems", name_includes: 'EverStitch 272 — 6"' },  unit: "roll", default_qty: 0 },
  { id: "ev_stitch_12",  label: 'EverStitch 272 — 12" x 300\'', match: { vendor: "Everest Systems", name_includes: 'EverStitch 272 — 12"' }, unit: "roll", default_qty: 0 },
  { id: "ev_stitch_20",  label: 'EverStitch 272 — 20" x 300\'', match: { vendor: "Everest Systems", name_includes: 'EverStitch 272 — 20"' }, unit: "roll", default_qty: 0 },
  { id: "ev_stitch_39",  label: 'EverStitch 272 — 39" x 300\'', match: { vendor: "Everest Systems", name_includes: 'EverStitch 272 — 39"' }, unit: "roll", default_qty: 0 },
  { id: "ev_stitch_40",  label: 'EverStitch 272 — 40" x 324\'', match: { vendor: "Everest Systems", name_includes: 'EverStitch 272 — 40"' }, unit: "roll", default_qty: 0 },
  // Silkoxy field add-ons
  { id: "ev_flashing_2",  label: "Silkoxy Flashing Grade — 2 Gal", match: { vendor: "Everest Systems", name_includes: "Silkoxy Flashing Grade — 2 Gal" }, unit: "pail", default_qty: 0 },
  { id: "ev_flashing_5",  label: "Silkoxy Flashing Grade — 5 Gal", match: { vendor: "Everest Systems", name_includes: "Silkoxy Flashing Grade — 5 Gal" }, unit: "pail", default_qty: 0 },
  { id: "ev_patch_2",     label: "Silkoxy Patch — 2 Gal",          match: { vendor: "Everest Systems", name_includes: "Silkoxy Patch — 2 Gal" },          unit: "pail", default_qty: 0 },
  { id: "ev_patch_5",     label: "Silkoxy Patch — 5 Gal",          match: { vendor: "Everest Systems", name_includes: "Silkoxy Patch — 5 Gal" },          unit: "pail", default_qty: 0 },
  { id: "ev_ever_tread",  label: "Silkoxy Ever-Tread Walk Pad",    match: { vendor: "Everest Systems", name_includes: "Ever-Tread Walk Pad" },            unit: "roll", default_qty: 0 },
  // AF Cleaner — qty in gallons, packs across 55-gal drums + 5-gal pails.
  { id: "ev_cleaner",     label: "AF Cleaner Concentrate",         match: { vendor: "Everest Systems", name_includes: "AF Cleaner Concentrate" },          unit: "gal",  default_qty: 0 },
  // SESCO granules — Sealtech orders by the PALLET (qty here = pallets). The
  // backing product_catalog rows are stored as 1 pallet = $bags_per_pallet ×
  // $/bag (e.g. BUFF = 56 × $8 = $448/pallet). Triggers $2,000 LTL freight.
  { id: "sesco_buff",        label: "Granules — Buff (56 bags/pallet)",      match: { vendor: "SESCO", name_includes: "buff" },        unit: "pallet", isGranule: true, default_qty: 0 },
  { id: "sesco_brown",       label: "Granules — Brown (30 bags/pallet)",     match: { vendor: "SESCO", name_includes: "brown" },       unit: "pallet", isGranule: true, default_qty: 0 },
  { id: "sesco_rainbow",     label: "Granules — Rainbow (30 bags/pallet)",   match: { vendor: "SESCO", name_includes: "rainbow" },     unit: "pallet", isGranule: true, default_qty: 0 },
  { id: "sesco_6_10_white",  label: "Granules — 6/10 White (56 bags/pallet)",match: { vendor: "SESCO", name_includes: "6/10 white" },  unit: "pallet", isGranule: true, default_qty: 0 },
  { id: "sesco_snow_white",  label: "Granules — Snow White (63 bags/pallet)",match: { vendor: "SESCO", name_includes: "snow white" },  unit: "pallet", isGranule: true, default_qty: 0 },
];

// Flat freight surcharge applied once per column when any SESCO granule line
// has qty > 0. Set by SESCO's published LTL rate.
const SESCO_GRANULE_FREIGHT = 2000;

// Pacific Coast/GAF add-ons — fabrics, silicone mastic (used on BOTH acrylic
// and silicone systems), repair tape and repair caps. NO seam tape here —
// seam tape is for EPDM applications only.
const GAF_ADDON_TEMPLATES = [
  { id: "gaf_fab_40", label: 'Premium Fabric 40" x 336\'', match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'premium fabric 40"' }, unit: "roll", default_qty: 0 },
  { id: "gaf_fab_24", label: 'Premium Fabric 24" x 300\'', match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'premium fabric 24"' }, unit: "roll", default_qty: 0 },
  { id: "gaf_fab_20", label: 'Premium Fabric 20" x 300\'', match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'premium fabric 20"' }, unit: "roll", default_qty: 0 },
  { id: "gaf_fab_16", label: 'Premium Fabric 16" x 300\'', match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'premium fabric 16"' }, unit: "roll", default_qty: 0 },
  { id: "gaf_fab_12", label: 'Premium Fabric 12" x 300\'', match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'premium fabric 12"' }, unit: "roll", default_qty: 0 },
  { id: "gaf_fab_6",  label: 'Premium Fabric 6" x 300\'',  match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'premium fabric 6"' },  unit: "roll", default_qty: 0 },
  { id: "gaf_fab_4",  label: 'Premium Fabric 4" x 300\'',  match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'premium fabric 4"' },  unit: "roll", default_qty: 0 },
  // Mastic / flashing — "flash, flashing and mastic in tubes, pails, drums
  // are all mastic". Silicone mastic for silicone jobs, brush-grade acrylic
  // flashing for acrylic jobs. Qty in gallons (auto-packs pail sizes) or tubes.
  { id: "gaf_mastic_wht",  label: "Silicone Mastic 2 Gal — White/Lt Gray", chemistry: "silicone", match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: "silicone mastic 2 gal - white" }, unit: "gal",  default_qty: 0 },
  { id: "gaf_mastic_blk",  label: "Silicone Mastic 2 Gal — Black",         chemistry: "silicone", match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: "silicone mastic 2 gal - black" }, unit: "gal",  default_qty: 0 },
  { id: "gaf_mastic_tube", label: "Silicone Mastic 20 oz Tube — White",    chemistry: "silicone", match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: "silicone mastic 20 oz" },         unit: "tube", default_qty: 0 },
  { id: "gaf_acrylic_flash", label: "Acrylic Flashing/Mastic — Brush Grade", chemistry: "acrylic", match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: "brush grade acrylic flashing" }, unit: "gal", default_qty: 0 },
  { id: "gaf_cleaner", label: "Roof Cleaner Concentrate", match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: "roof cleaner concentrate" }, unit: "gal", default_qty: 0 },
  { id: "gaf_repair_2", label: 'GAF Repair Tape 2" x 50\'', match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: 'repair tape 2"' },    unit: "roll", default_qty: 0 },
  { id: "gaf_repair_caps", label: "Repair Caps",           match: { vendor: "Pacific Coast Building Products/ GAF", name_includes: "repair caps" },        unit: "roll", default_qty: 0 },
];

// Vendor "family" — decides which add-on rail + template list a vendor uses
// and which columns an add-on may cost into (no cross-brand bleed).
const vendorFamily = (v) => {
  const s = (v || "").toLowerCase();
  if (s.includes("everest")) return "everest";
  if (s.includes("gaf") || s.includes("abc supply")) return "gaf";
  return "wc"; // Western Colloid + generic vendors use the SKU-based rail
};
const TEMPLATES_BY_FAMILY = { everest: EVEREST_ADDON_TEMPLATES, gaf: GAF_ADDON_TEMPLATES, wc: ADDON_TEMPLATES };


// One-click "stress points bundle" — typical fabric strip qty for a smaller
// commercial roof (penetrations, edges, drains). Tweak via the inputs after.
const STRESS_POINTS_PRESET = {
  fabric_4in:  1,
  fabric_6in:  2,
  fabric_12in: 1,
};

// Persisted UI preferences key in localStorage.
const PREFS_KEY = "calc:prefs:v1";

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function savePrefs(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* ignore quota errors */ }
}

const MAX_COMPARE = 3;

/** Classify a product's package size into a coarse "container kind" so we
 *  can let the user veto tote / drum / pail delivery based on site access. */
function classifyContainer(p) {
  if ((p.unit || "").toLowerCase() === "roll") return "roll";
  const s = Number(p.package_size || 0);
  if (s >= 200) return "tote";
  if (s >= 30)  return "drum";
  return "pail";
}

/** Group products by their (vendor, sku) — each "logical" product can have
 *  multiple container-size SKUs (5-gal pail / 55-gal drum / 275-gal tote). */
function groupBySku(products) {
  const map = new Map();
  for (const p of products) {
    // Empty SKU must NOT group unrelated products into one container family —
    // fall back to the product's own id (no size variants).
    const key = p.sku ? `${p.vendor}||${p.sku}` : `${p.vendor}||id:${p.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  for (const arr of map.values()) arr.sort((a, b) => b.package_size - a.package_size);
  return map;
}

/** Greedy fill of `qtyNeeded` units across the given containers (sorted big→small).
 *  Returns [{product, qty (= # of THIS container), gallons (= qty × pkg_size), cost}]. */
function packContainers(qtyNeeded, containers) {
  const out = [];
  let remaining = qtyNeeded;
  // Floating-point safety: snap a number to its nearest integer when within
  // an epsilon (otherwise 5.000076 ceils to 6 instead of the obvious 5).
  const snap = (n) => {
    const r = Math.round(n);
    return Math.abs(n - r) < 1e-4 ? r : n;
  };
  for (let i = 0; i < containers.length; i++) {
    const c = containers[i];
    const isLast = i === containers.length - 1;
    if (remaining <= 0) break;
    let count;
    const ratio = snap(remaining / c.package_size);
    if (isLast) {
      // Round UP for the final (smallest) container so we always cover the qty.
      count = Math.ceil(ratio);
    } else {
      // For larger containers, only buy "whole" ones that don't overshoot.
      count = Math.floor(ratio);
    }
    if (count > 0) {
      const gallons = count * c.package_size;
      const cost = count * (c.unit_price * c.package_size);
      out.push({ product: c, qty: count, gallons, cost });
      remaining = snap(remaining - gallons);
    }
  }
  return out;
}

function bandWarrantyLabel(years) {
  if (!years) return "";
  return `${years}-Year`;
}

export default function Calculator() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const initialDealId = params.get("deal");

  const [products, setProducts] = useState([]);
  const [systems, setSystems] = useState([]);
  const [recipes, setRecipes] = useState({}); // {system_id: [recipe_rows]}
  const [settings, setSettings] = useState({ markup_pct: 20, handling_pct: 12, handling_basis: "marked_up", waste_pct: 0, overhead_pct: 20, profit_pct: 30 });
  // Session overrides for Shipping (markup) % and Handling % — blank means
  // "use the Calculator Settings default". Never written back to settings.
  const [shippingPctOv, setShippingPctOv] = useState("");
  const [handlingPctOv, setHandlingPctOv] = useState("");
  const effMarkupPct = shippingPctOv === "" ? Number(settings.markup_pct || 0) : Number(shippingPctOv);
  const effHandlingPct = handlingPctOv === "" ? Number(settings.handling_pct || 0) : Number(handlingPctOv);
  const [loading, setLoading] = useState(true);

  // Persisted prefs (loaded once on mount via the lazy initialiser).
  const initialPrefs = loadPrefs() || {};
  const [selectedVendor, setSelectedVendor] = useState(initialPrefs.vendor || "Western Colloid");
  const [secondVendor, setSecondVendor] = useState("");
  const [totalSf, setTotalSf] = useState("");
  const [waste, setWaste] = useState(initialPrefs.waste ?? 0);
  const [allowedSizes, setAllowedSizes] = useState(initialPrefs.allowedSizes || { tote: true, drum: true, pail: true });
  const [selectedSystemIds, setSelectedSystemIds] = useState([]);
  // Per-coat configuration — {`${systemId}:${recipeRowId}`: {include, passes, gps}}.
  // The coat rows are the single source of truth for application quantities:
  // effective rate = passes × gps (per pass). Missing key = smart default
  // derived from the recipe rate split by the max-GPS-per-pass rules
  // (Silicone 2.5 / base coats 3.0 / acrylic top coats 1.5).
  const [coatConfig, setCoatConfig] = useState({});
  const [addons, setAddons] = useState({});      // {addon_id: qty} (shared across compared systems)
  // Two free-form rows the rep can type into on Western Colloid jobs —
  // label + customer-facing $ amount. Added flat to every compared system's
  // customer price (after OH/Profit, treated as retail dollars the rep
  // already priced themselves). Empty rows are ignored.
  const [customAddons, setCustomAddons] = useState([
    { label: "", cost: "" },
    { label: "", cost: "" },
    { label: "", cost: "" },
    { label: "", cost: "" },
    { label: "", cost: "" },
    { label: "", cost: "" },
  ]);
  // Per-warranty-band labor dollar amount. Rep types this per job — different
  // every roof. Map: {25: 6300, 20: 5800, 15: 4500, 10: 3500}. Persisted to the
  // deal as labor_25yr_add / _20yr_add / _15yr_add / _10yr_add when "Set" fires.
  const [laborBySystem, setLaborBySystem] = useState({});
  // Per-warranty OH and Profit % overrides (default to global settings).
  // Stored on the deal as overhead_25yr_pct / profit_25yr_pct etc.
  const [overheadBySystem, setOverheadBySystem] = useState({});
  const [profitBySystem,   setProfitBySystem]   = useState({});
  // Per-warranty NDL (No-Dollar-Limit) toggle — Everest Systems only. Standard
  // Everest warranty = $1,000 flat, NDL = $3,500 flat, both regardless of roof
  // size. Persisted to the deal as warranty_*_ndl booleans.
  const [ndlBySystem, setNdlBySystem] = useState({});
  // Per-warranty combined NDL + Hail Rider $ — Western Colloid 20/25-yr.
  // Customer accepts NDL upgrade and gets Hail Rider bundled — one price
  // per tier on the scope. `undefined` means "use auto-default from SF".
  // Persisted to deal.hail_rider_{20,25}yr_add on Set Option.
  const [hailRiderBySystem, setHailRiderBySystem] = useState({});

  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(initialDealId || "");
  const [deal, setDeal] = useState(null);
  // Property record for the picked deal. Loaded lazily via the deal's
  // `property_id` — used only to display the site address in the sticky
  // "Working On" banner so the rep never loses context while comparing
  // systems below.
  const [property, setProperty] = useState(null);
  const [savingToDeal, setSavingToDeal] = useState(false);
  const [generatingPo, setGeneratingPo] = useState(false);
  // "estimate" = pre-signature compare; "materials" = post-signature push-to-PO.
  // Default auto-switches based on whether the chosen deal has scope_signed_at.
  const [mode, setMode] = useState("estimate");

  // Save prefs whenever the user changes a "remembered" setting
  useEffect(() => {
    savePrefs({ vendor: selectedVendor, waste, allowedSizes });
  }, [selectedVendor, waste, allowedSizes]);

  useEffect(() => {
    Promise.all([
      api.get("/products"),
      api.get("/systems"),
      api.get("/calculator/settings"),
      api.get("/deals"),
    ]).then(([p, s, st, dl]) => {
      setProducts(p.data || []);
      setSystems(s.data || []);
      setSettings((prev) => ({ ...prev, ...(st.data || {}) }));
      // Only override waste from settings if there is no localStorage pref
      if (initialPrefs.waste === undefined) {
        setWaste(Number((st.data || {}).waste_pct || 0));
      }
      // Sort deals by most-recently-updated first, drop trashed/test-only ones to bottom
      const dealsList = (dl.data || [])
        .filter((d) => !d.is_deleted)
        .sort((a, b) => {
          const ad = a.updated_at || a.created_at || "";
          const bd = b.updated_at || b.created_at || "";
          return bd.localeCompare(ad);
        });
      setDeals(dealsList);
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  // Load the picked deal whenever the dropdown changes
  useEffect(() => {
    if (!selectedDealId) {
      setDeal(null);
      setProperty(null);
      return;
    }
    api.get(`/deals/${selectedDealId}`).then(async (r) => {
      setDeal(r.data);
      // Property record for the picked deal. Loaded lazily via the deal's
      // `property_id` — used both for the sticky banner (address) and as
      // the SF fallback below.
      let propertyDoc = null;
      if (r.data.property_id) {
        try {
          const pr = await api.get(`/properties/${r.data.property_id}`);
          propertyDoc = pr.data || null;
        } catch { propertyDoc = null; }
      }
      setProperty(propertyDoc);
      // Smart SF preference order:
      //   1. deal.total_sqft  — backend-computed roof+parapet total
      //      (= property_sqft + perimeter_lnft × avg_parapet_height)
      //   2. deal.property_sqft  — bare floor area (legacy / no parapet)
      //   3. property record's roof_area / square_footage
      // The Calculator drives material PO counts so we MUST use the
      // parapet-inclusive figure when it's available.
      let sf = r.data.total_sqft || r.data.property_sqft || r.data.total_sf;
      if (!sf && propertyDoc) sf = propertyDoc.roof_area || propertyDoc.square_footage;
      if (sf) setTotalSf(String(sf));
      // Per-SYSTEM overrides reset on deal switch. Effective values fall back
      // to the deal's saved per-warranty-band fields inside computeBom, so
      // same-warranty silicone + acrylic columns can hold DIFFERENT labor.
      setLaborBySystem({});
      setOverheadBySystem({});
      setProfitBySystem({});
      setNdlBySystem({});
      setHailRiderBySystem({});
      // Restore the rep's saved coat configuration (passes × GPS per coat).
      setCoatConfig(r.data.calc_coat_config || {});
      // Re-hydrate the rep's typed Custom Add-Ons (rows that have a label
      // and a cost). Always keep 6 visible rows — pad with empties if the
      // deal had fewer saved. Extra empty rows give reps room for
      // equipment rentals (scissor lift, boom, blower, generator, etc.)
      // plus any other one-off line items without hitting a row limit.
      const savedAddons = Array.isArray(r.data.calc_custom_addons)
        ? r.data.calc_custom_addons.map((x) => ({
            label: String(x.label || ""),
            cost: x.cost != null ? String(x.cost) : "",
          }))
        : [];
      const ROW_COUNT = 6;
      const padded = Array.from({ length: Math.max(ROW_COUNT, savedAddons.length) }, (_, i) =>
        savedAddons[i] || { label: "", cost: "" }
      );
      setCustomAddons(padded);
      // Auto-switch to Materials & PO mode if the customer has signed off
      // (Darren can manually toggle back to Estimate if needed).
      if (r.data.scope_signed_at) setMode("materials");
      else setMode("estimate");
      // Keep ?deal=<id> in the URL in sync so refresh keeps the selection
      const next = new URLSearchParams(params);
      next.set("deal", selectedDealId);
      setParams(next, { replace: true });
    }).catch(() => setDeal(null));
  }, [selectedDealId]);

  const productById = useMemo(() => {
    const m = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  const groupedBySku = useMemo(() => groupBySku(products), [products]);

  const toggleSystem = async (sysId) => {
    if (selectedSystemIds.includes(sysId)) {
      setSelectedSystemIds(selectedSystemIds.filter((x) => x !== sysId));
      return;
    }
    if (selectedSystemIds.length >= MAX_COMPARE) {
      toast.warning(`Compare up to ${MAX_COMPARE} systems at a time`);
      return;
    }
    setSelectedSystemIds([...selectedSystemIds, sysId]);
    if (!recipes[sysId]) {
      try {
        const r = await api.get(`/systems/${sysId}/recipe`);
        setRecipes((prev) => ({ ...prev, [sysId]: r.data || [] }));
      } catch (e) {
        toast.error("Could not load recipe");
      }
    }
  };

  /** Max gallons-per-square a single pass can physically carry. */
  const maxGpsPerPass = (system, r) => {
    const nm = `${productById[r.product_id]?.name || ""} ${r.notes || ""}`.toLowerCase();
    const chem = (system.system_type || "").toLowerCase();
    if (/silicone/.test(chem) || /silicone/.test(nm)) return 2.5;
    if (/top/.test(nm)) return 1.5; // acrylic top coats
    return 3.0;                     // base / reinforced coats
  };

  /** Smart default for a coat row — recipe rate auto-split into passes. */
  const defaultCoatCfg = (system, r) => {
    const rate = Number(r.coverage_rate || 0);
    const maxPP = maxGpsPerPass(system, r);
    const passes = Math.max(1, Math.ceil(rate / maxPP - 1e-9));
    return { include: true, passes, gps: Math.round((rate / passes) * 100) / 100 };
  };

  const coatCfg = (system, r) => coatConfig[`${system.id}:${r.id}`] || defaultCoatCfg(system, r);

  /** Compute a full Bill-of-Materials column for one system. */
  const computeBom = (system) => {
    const sf = Number(totalSf) || 0;
    const waste_factor = 1 + (Number(waste) || 0) / 100;
    const recipe = recipes[system.id] || [];
    // Coat rows are the source of truth: effective GPS = passes × gps.
    const coats = [];
    const lines = [];
    for (const r of recipe) {
      const product = productById[r.product_id];
      if (!product) continue;
      const isGalLine = r.coverage_basis === "per_100sf" && product.unit === "gal";
      let effRate = Number(r.coverage_rate || 0);
      if (isGalLine) {
        const cfg = coatCfg(system, r);
        coats.push({
          key: `${system.id}:${r.id}`,
          name: product.name,
          include: cfg.include,
          passes: cfg.passes,
          gps: cfg.gps,
        });
        if (!cfg.include) continue; // coat toggled off
        effRate = (Number(cfg.passes) || 1) * (Number(cfg.gps) || 0);
      }
      // Resolve qty needed based on coverage basis
      let qtyNeeded = 0;
      if (r.coverage_basis === "per_100sf") qtyNeeded = (sf / 100) * effRate;
      else if (r.coverage_basis === "per_sf") qtyNeeded = sf * Number(r.coverage_rate || 0);
      else if (r.coverage_basis === "per_each_optional") qtyNeeded = Number(r.coverage_rate || 0);
      else if (r.coverage_basis === "per_lf") qtyNeeded = Number(r.coverage_rate || 0);
      qtyNeeded = qtyNeeded * waste_factor;

      // Container packing across sibling SKUs (same vendor + sku), filtered
      // by what containers the site can physically accept.
      const key = `${product.vendor}||${product.sku}`;
      const siblings = groupedBySku.get(key) || [product];
      const containers = siblings.filter((c) => {
        const k = classifyContainer(c);
        if (k === "roll") return true;        // fabric is unaffected by tote/drum/pail toggles
        return allowedSizes[k];
      });
      // Safety net: if every allowed size was filtered out (e.g. only pails
      // available but pails disabled), fall back to siblings so we still
      // surface a number rather than silently dropping the line.
      const useContainers = containers.length > 0 ? containers : siblings;
      const packed = packContainers(qtyNeeded, useContainers);
      const lineCost = packed.reduce((s, x) => s + x.cost, 0);
      const lineQty  = packed.reduce((s, x) => s + x.gallons, 0);
      lines.push({
        recipe_row: r,
        product, qtyNeeded, qtyPacked: lineQty,
        packed, lineCost,
        coatCfg: isGalLine ? coatCfg(system, r) : null,
      });
    }

    // Apply add-ons (shared across all systems). Each entry in `addons` maps an
    // addon-template id to a qty. Two template families exist:
    //   - ADDON_TEMPLATES         → SKU-based (Western Colloid)
    //   - EVEREST_ADDON_TEMPLATES → vendor+name-based (Everest + SESCO granules)
    const addonLines = [];
    let granuleQtyTotal = 0;
    // Column chemistry — chemistry-tagged add-ons (silicone vs acrylic mastic)
    // only land on matching systems, even though add-on qty is shared.
    const colChem = /silicone/i.test(system.system_type || "") ? "silicone"
      : /acrylic/i.test(system.system_type || "") ? "acrylic" : null;
    for (const [aid, qty] of Object.entries(addons)) {
      if (!qty || qty <= 0) continue;
      const evTpl = EVEREST_ADDON_TEMPLATES.find((a) => a.id === aid);
      const gafTpl = GAF_ADDON_TEMPLATES.find((a) => a.id === aid);
      const matchTpl = evTpl || gafTpl;
      const tpl = matchTpl || ADDON_TEMPLATES.find((a) => a.id === aid);
      if (!tpl) continue;
      // Brand-family filter — WC add-ons only cost into WC columns, GAF
      // add-ons into GAF/ABC columns, Everest into Everest columns.
      const tplFam = evTpl ? "everest" : gafTpl ? "gaf" : "wc";
      if (tplFam !== vendorFamily(system.vendor)) continue;
      if (tpl.chemistry && colChem && tpl.chemistry !== colChem) continue;
      // Resolve the catalog row(s) backing this add-on.
      let matching;
      if (matchTpl) {
        const v = (matchTpl.match.vendor || "").toLowerCase();
        const n = (matchTpl.match.name_includes || "").toLowerCase();
        matching = products.filter(
          (p) => (p.vendor || "").toLowerCase() === v &&
                 (p.name   || "").toLowerCase().includes(n)
        );
      } else {
        matching = products.filter((p) => p.sku === tpl.sku);
      }
      if (matching.length === 0) continue;
      const filtered = matching.filter((c) => {
        const k = classifyContainer(c);
        if (k === "roll") return true;
        return allowedSizes[k];
      });
      const containers = (filtered.length ? filtered : matching).slice().sort((a, b) => b.package_size - a.package_size);
      const packed = packContainers(Number(qty), containers);
      const lineCost = packed.reduce((s, x) => s + x.cost, 0);
      const lineQty  = packed.reduce((s, x) => s + x.gallons, 0);
      addonLines.push({
        addon: tpl, qtyNeeded: Number(qty), qtyPacked: lineQty, packed, lineCost,
      });
      if (evTpl && evTpl.isGranule) granuleQtyTotal += Number(qty);
    }
    // SESCO LTL freight — flat $2,000 per column when ANY granule is ordered.
    if (granuleQtyTotal > 0) {
      addonLines.push({
        addon: { id: "sesco_freight", label: "SESCO Granule Freight (LTL)", unit: "flat" },
        qtyNeeded: 1, qtyPacked: 1,
        packed: [{ qty: 1, gallons: 0, cost: SESCO_GRANULE_FREIGHT,
                   product: { name: "SESCO LTL Freight", vendor: "SESCO",
                              unit: "flat", package_size: 1 } }],
        lineCost: SESCO_GRANULE_FREIGHT,
      });
    }

    const rawCost = lines.reduce((s, l) => s + l.lineCost, 0) + addonLines.reduce((s, l) => s + l.lineCost, 0);
    const markedUp = rawCost * (1 + effMarkupPct / 100);
    const handlingBase = settings.handling_basis === "raw" ? rawCost : markedUp;
    const handling = handlingBase * (effHandlingPct / 100);
    // Warranty up-charge — Everest gets vendor-specific pricing; other vendors
    // read the rep-entered flat $ from the deal.
    //   Standard: $1,000 flat (any band — no inspection). ALWAYS included in
    //             the customer's base price on Everest jobs.
    //   NDL:      $3,000 third-party inspection + per-SF rate based on warranty
    //             band ($0.06/SF @ 10-yr, $0.09/SF @ 15-yr, $0.12/SF @ 20-yr).
    //             Shown to the customer as an OPTIONAL upgrade — never folded
    //             into the base price. 5-yr has no NDL.
    const WARRANTY_ADD_FIELD = { 25: "warranty_25yr_add", 20: "warranty_20yr_add",
                                  15: "warranty_15yr_add", 10: "warranty_10yr_add" };
    const NDL_PER_SF = { 10: 0.06, 15: 0.09, 20: 0.12 };
    const isEverest = (system.vendor || "").toLowerCase().includes("everest");
    // GAF (Pacific Coast) — Standard warranty is baked into EVERY option:
    // max(per-SF, min) + $1,000 inspection. NDL shows as an optional upgrade
    // priced as the DIFFERENCE between NDL and Standard (inspection cancels).
    const isGaf = (system.vendor || "").toLowerCase().includes("gaf")
      || (system.vendor || "").toLowerCase().includes("abc supply"); // ABC sells GAF products — same warranty schedule
    const GAF_WARRANTY = {
      10: { std: 0.02, stdMin: 500,  ndl: 0.05, ndlMin: 1000 },
      15: { std: 0.03, stdMin: 750,  ndl: 0.07, ndlMin: 1250 },
      20: { std: 0.04, stdMin: 1000, ndl: 0.10, ndlMin: 1500 },
      25: { std: 0.05, stdMin: 1250, ndl: 0.12, ndlMin: 2000 },
    };
    const gafCfg = isGaf ? GAF_WARRANTY[system.warranty_years] : null;
    const ndlAvailable = (isEverest && (system.warranty_years in NDL_PER_SF)) || !!gafCfg;
    const isNdl = ndlAvailable && !!(ndlBySystem[system.id] ?? deal?.[`warranty_${system.warranty_years}yr_ndl`]);

    // ---- NDL Warranty Upgrade + Hail Rider (Western Colloid 20/25-yr) ----
    // On WC systems, the NDL Warranty Upgrade for 20/25-yr tiers BUNDLES
    // the Hail Rider — customer pays one combined price per tier. SF is
    // rounded UP to the next 1,000 SF per SealTech ops rule.
    //   20-yr: NDL $0.15/SF (min $1,750) + Hail Rider $0.04/SF (min $750)
    //   25-yr: NDL $0.20/SF (min $2,000) + Hail Rider $0.05/SF (min $1,000)
    // Rep can override the combined $ per column, or clear to 0 to omit
    // the optional line from the customer scope.
    const isWesternColloid = (system.vendor || "").toLowerCase().includes("western colloid");
    const hailRiderEligible = isWesternColloid && [20, 25].includes(system.warranty_years);
    // SF rounded UP to next 1,000 for the customer-facing NDL/Hail math.
    const sfRounded = sf > 0 ? Math.ceil(sf / 1000) * 1000 : 0;
    const NDL_HAIL_RATES = {
      20: { ndlPerSf: 0.15, ndlMin: 1750, hailPerSf: 0.04, hailMin: 750 },
      25: { ndlPerSf: 0.20, ndlMin: 2000, hailPerSf: 0.05, hailMin: 1000 },
    };
    const hrCfg = NDL_HAIL_RATES[system.warranty_years];
    const hailRiderDefault = hailRiderEligible && hrCfg
      ? Math.max(sfRounded * hrCfg.ndlPerSf, hrCfg.ndlMin)
        + Math.max(sfRounded * hrCfg.hailPerSf, hrCfg.hailMin)
      : 0;
    const dealHailRider = deal?.[`hail_rider_${system.warranty_years}yr_add`];
    // A saved 0 counts as "unset" — the computed NDL+Hail default applies
    // (it's an OPTIONAL price; reps can still clear it in-session).
    const savedHailRider = hailRiderBySystem[system.id]
      ?? ((dealHailRider !== undefined && dealHailRider !== null && Number(dealHailRider) > 0) ? Number(dealHailRider) : undefined);
    const hailRiderAmount = hailRiderEligible
      ? (savedHailRider !== undefined ? Number(savedHailRider) : Math.round(hailRiderDefault))
      : 0;

    // Standard warranty $ — what's baked into the base customer price.
    // The rep-entered flat $ fallback only applies to PRIMARY-vendor columns:
    // on mixed-manufacturer jobs the deal's warranty band fields belong to the
    // primary brand's math and must not bleed into the second brand's base.
    const standardWarranty = isEverest ? 1000
      : gafCfg ? Math.max(gafCfg.std * sf, gafCfg.stdMin) + 1000
      : (
      (deal && (system.vendor || "") === selectedVendor && WARRANTY_ADD_FIELD[system.warranty_years])
        ? Number(deal[WARRANTY_ADD_FIELD[system.warranty_years]] || 0)
        : 0
    );
    // Raw NDL surcharge (before OH+Profit) — Everest + GAF only.
    const ndlSurchargeRaw = !ndlAvailable ? 0
      : isEverest ? (3000 + (NDL_PER_SF[system.warranty_years] || 0) * sf - 1000)
      : (Math.max(gafCfg.ndl * sf, gafCfg.ndlMin) - Math.max(gafCfg.std * sf, gafCfg.stdMin));
    // Display warranty row label drives off the toggle (Standard vs NDL),
    // but the base price math always uses Standard.
    const warrantyAdd = isEverest ? standardWarranty : standardWarranty;
    // Labor — per-COLUMN input the rep types (keyed by system id so two
    // same-warranty systems, e.g. 20-yr silicone vs 20-yr acrylic, hold
    // different amounts). Falls back to the deal's saved band value.
    const laborAdd = Number((laborBySystem[system.id] ?? deal?.[`labor_${system.warranty_years}yr_add`]) || 0);
    // Per-column Overhead / Profit % (per-system override → deal band value →
    // global Settings default). 0% IS a valid override → don't collapse with `||`.
    const nOU = (v) => (v === null || v === undefined || v === "") ? undefined : Number(v);
    const ohPct = overheadBySystem[system.id] ?? nOU(deal?.[`overhead_${system.warranty_years}yr_pct`]) ?? Number(settings.overhead_pct || 0);
    const prPct = profitBySystem[system.id]   ?? nOU(deal?.[`profit_${system.warranty_years}yr_pct`])   ?? Number(settings.profit_pct   || 0);
    // Subtotal before OH&P — Standard warranty baseline (the customer's base).
    const subtotal = markedUp + handling + warrantyAdd + laborAdd;
    const overhead = subtotal * (ohPct / 100);
    const profit   = (subtotal + overhead) * (prPct / 100);
    const customerBase = subtotal + overhead + profit;
    // Custom add-ons (the 2 free-form rows on the WC rail). These are retail
    // dollars the rep priced themselves — added flat AFTER OH+Profit so we
    // don't double-mark-up. Empty rows skipped.
    const customAddonTotal = (customAddons || []).reduce((acc, row) => {
      const c = Number(row?.cost || 0);
      return acc + (isFinite(c) ? c : 0);
    }, 0);
    // NDL upgrade $ = same compounded markup applied to the surcharge so the
    // upgrade row carries identical margin as the base. This is what we
    // publish on the PDF's "[OPTIONAL] Manufacturer Warranty" line.
    const ndlUpgradeDelta = ndlSurchargeRaw * (1 + ohPct / 100) * (1 + prPct / 100);
    // Displayed customer price = base + NDL only if rep ticked the toggle in
    // the calculator. The toggle is for the rep's internal pricing reference;
    // the PDF always shows base as the headline and NDL as an option.
    const customer = customerBase + (isNdl ? ndlUpgradeDelta : 0) + customAddonTotal;
    const pricePerSf = sf > 0 ? customer / sf : 0;

    return { system, lines, coats, addonLines, rawCost, markedUp, handling, warrantyAdd, isEverest, isGaf, ndlAvailable, isNdl, ndlUpgradeDelta, customerBase, customAddonTotal, laborAdd, subtotal, overhead, profit, ohPct, prPct, customer, pricePerSf, hailRiderEligible, hailRiderAmount, hailRiderDefault, markupPct: effMarkupPct, handlingPct: effHandlingPct };
  };

  const columns = useMemo(() => {
    return selectedSystemIds
      .map((id) => systems.find((s) => s.id === id))
      .filter(Boolean)
      .map(computeBom);
  }, [selectedSystemIds, recipes, products, totalSf, waste, settings, addons, customAddons, allowedSizes, deal, laborBySystem, overheadBySystem, profitBySystem, ndlBySystem, hailRiderBySystem, coatConfig, effMarkupPct, effHandlingPct, selectedVendor]);

  // Warn the rep on browser/tab navigation if any Calculator column has
  // labor/OH/Profit values that differ from what's persisted on the deal.
  // Prevents the "I typed $30k labor and it never made it to the deal"
  // trap that surfaced on 2278 Mannatt.
  const hasAnyUnsaved = useMemo(() => {
    if (!deal) return false;
    return columns.some((col) => {
      const y = col.system.warranty_years;
      const persistedLabor = Number(deal?.[`labor_${y}yr_add`] ?? 0);
      if (Number(col.laborAdd || 0) !== persistedLabor) return true;
      const oh = overheadBySystem[col.system.id];
      if (oh !== undefined && Number(oh) !== Number(deal?.[`overhead_${y}yr_pct`] ?? oh)) return true;
      const pr = profitBySystem[col.system.id];
      if (pr !== undefined && Number(pr) !== Number(deal?.[`profit_${y}yr_pct`] ?? pr)) return true;
      return false;
    });
  }, [deal, columns, overheadBySystem, profitBySystem]);

  useEffect(() => {
    if (!hasAnyUnsaved) return;
    const onBeforeUnload = (e) => {
      // Modern browsers ignore the custom text but honor preventDefault.
      e.preventDefault();
      e.returnValue = "You have unsaved Calculator values (Labor, Overhead, or Profit) that will be lost.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasAnyUnsaved]);

  // ────────────────────────────────────────────────────────────────────
  //   Calculator → Deal action handlers (split by mode)
  // ────────────────────────────────────────────────────────────────────
  const PROPOSAL_FIELD_BY_WARRANTY = {
    25: "proposal_option_25yr", // Option A
    20: "proposal_option_1",    // Option B
    15: "proposal_option_2",    // Option C
    10: "proposal_option_3",    // Option D
  };
  const OPTION_LETTER = { 25: "A", 20: "B", 15: "C", 10: "D" };

  // Flexible option slots — a deal has four price slots. Natural mapping is
  // by warranty (A=25...D=10), but when silicone + acrylic are quoted at the
  // SAME tier the second system takes the first free slot and carries its own
  // scope label so both prices appear on the customer's PDF.
  const OPTION_SLOTS = [
    { field: "proposal_option_25yr", letter: "A", years: 25 },
    { field: "proposal_option_1",    letter: "B", years: 20 },
    { field: "proposal_option_2",    letter: "C", years: 15 },
    { field: "proposal_option_3",    letter: "D", years: 10 },
  ];
  const slotForColumn = (col, claimed = {}) => {
    // occupant: "" free · system id · "?" legacy price with unknown system
    const occupant = (s) => claimed[s.field]
      ?? (deal?.[`${s.field}_system_id`] || (Number(deal?.[s.field] || 0) > 0 ? "?" : ""));
    const natural = OPTION_SLOTS.find((s) => s.years === col.system.warranty_years);
    if (natural) {
      const o = occupant(natural);
      // legacy "?" occupant on the natural slot = old single-system push → overwrite
      if (!o || o === col.system.id || o === "?") return natural;
    }
    return OPTION_SLOTS.find((s) => {
      const o = occupant(s);
      return !o || o === col.system.id;
    }) || null;
  };
  const brandCode = (v) => {
    const s = (v || "").toLowerCase();
    if (s.includes("everest")) return "EV";
    if (s.includes("western colloid")) return "WC";
    if (s.includes("gaf") || s.includes("pacific") || s.includes("abc supply")) return "GAF";
    return (v || "").split(" ")[0].toUpperCase().slice(0, 4);
  };
  const slotLabel = (col) => {
    const t = col.system.system_type || "";
    const chem = /acrylic/i.test(t) ? "Acrylic" : /silicone/i.test(t) ? "Silicone" : (t || "System");
    const b = brandCode(col.system.vendor);
    return `${col.system.warranty_years}-Year ${b ? b + " " : ""}${chem} — Standard Warranty`;
  };
  /** Per-slot optional warranty $ — GAF/EV = NDL upgrade delta, WC = NDL + Hail Rider. */
  const slotWarrantyAdd = (col) => Math.round((
    (col.isEverest || col.isGaf) ? (col.ndlUpgradeDelta || 0)
    : col.hailRiderEligible ? (col.hailRiderAmount || 0)
    : 0
  ) * 100) / 100;
  const scopeManufacturerFor = (col) => (col.isGaf ? "GAF" : col.isEverest ? "Everest Systems" : "");

  /**
   * Map a Calculator system's `system_type` to the `proposed_roof_type`
   * enum on the Deal. This is what drives which scope PDF template gets
   * rendered — without this, pushing a Silicone job from the Calculator
   * would leave the deal set to whatever it was created with (usually
   * "TPO Over-Lay" default) and the scope would come out as "TPO Layover".
   *
   * Only returns a value for system_types where the mapping is unambiguous;
   * unknown types leave `proposed_roof_type` untouched so we never break
   * an unrelated scope the rep already dialled in manually.
   */
  const roofTypeForSystem = (sys) => {
    const t = (sys?.system_type || "").toLowerCase();
    if (t === "silicone") return "Silicone";
    if (t === "farm") return "FARM (Fluid Applied Reinforced Membrane)";
    // "All-Acrylic" is a coating chemistry not present in the Deal roof-type
    // enum — leave the field alone so the rep can still pick from the full
    // dropdown on the deal page.
    return null;
  };

  /** ESTIMATE mode: only write the Customer Price into the matching
   *  proposal_option_* field on the deal. No cost lines, no PO. */
  const setOptionOnDeal = async (col) => {
    if (!deal) { toast.warning("Pick a deal from the dropdown first"); return; }
    const slot = slotForColumn(col);
    if (!slot) {
      toast.error("All four proposal option slots are taken — clear one on the deal first");
      return;
    }
    const field = slot.field;
    const letter = slot.letter;
    const LABOR_ADD_FIELD = { 25: "labor_25yr_add", 20: "labor_20yr_add", 15: "labor_15yr_add", 10: "labor_10yr_add" };
    const OH_FIELD     = { 25: "overhead_25yr_pct", 20: "overhead_20yr_pct", 15: "overhead_15yr_pct", 10: "overhead_10yr_pct" };
    const PR_FIELD     = { 25: "profit_25yr_pct",   20: "profit_20yr_pct",   15: "profit_15yr_pct",   10: "profit_10yr_pct" };
    const WAR_ADD_FIELD = { 25: "warranty_25yr_add", 20: "warranty_20yr_add", 15: "warranty_15yr_add", 10: "warranty_10yr_add" };
    const NDL_FIELD     = { 25: "warranty_25yr_ndl", 20: "warranty_20yr_ndl", 15: "warranty_15yr_ndl", 10: "warranty_10yr_ndl" };
    // Everest jobs: the customer's headline price ALWAYS shows the Standard-
    // warranty base ($1,000 baked in). The NDL upgrade $ travels via the
    // `warranty_*_add` field — the spec sheet renders it under "[OPTIONAL]
    // Manufacturer Warranty" so the customer can opt-in separately.
    // Custom Add-Ons are baked in — the scope lists them as price-hidden
    // "included" bullets, so the headline option $ must cover them.
    const baseForDeal = (col.isEverest || col.isGaf)
      ? col.customerBase + (col.customAddonTotal || 0)
      : col.customer;
    const customerRounded = Math.round(baseForDeal * 100) / 100;
    const laborField = LABOR_ADD_FIELD[col.system.warranty_years];
    const ohField    = OH_FIELD[col.system.warranty_years];
    const prField    = PR_FIELD[col.system.warranty_years];
    const warField   = WAR_ADD_FIELD[col.system.warranty_years];
    const ndlField   = NDL_FIELD[col.system.warranty_years];
    setSavingToDeal(true);
    try {
      const body = { ...deal, [field]: customerRounded };
      body[`${field}_system_id`] = col.system.id;
      body[`${field}_label`] = slotLabel(col);
      body[`${field}_warranty_add`] = slotWarrantyAdd(col);
      const brand = scopeManufacturerFor(col);
      if (brand) body.scope_manufacturer = brand;
      // Persist the SF the rep typed in the Calculator so the Scope PDF
      // "Install approximately N SF (S SQ)" bullet reflects reality instead
      // of falling back to 0. Only overwrites if the rep typed something —
      // an empty SF box leaves deal.total_sqft untouched.
      const typedSf = Number(totalSf);
      if (typedSf > 0) body.total_sqft = typedSf;
      if (laborField) body[laborField] = Math.round((col.laborAdd || 0) * 100) / 100;
      if (ohField) body[ohField] = col.ohPct;
      if (prField) body[prField] = col.prPct;
      // Auto-align the deal's proposed roof type — but never clobber a deal
      // already set to the combined "Acrylic/Silicone" scope.
      const newRoofType = roofTypeForSystem(col.system);
      if (newRoofType && deal.proposed_roof_type !== "Acrylic/Silicone") body.proposed_roof_type = newRoofType;
      // Persist the rep's typed custom add-ons (e.g. Metal Flashing $650) so
      // the spec sheet PDF can render them as visible scope-inclusion bullets
      // — and so re-opening the calc on this deal restores the same rows.
      // Only non-empty rows are saved.
      body.calc_custom_addons = (customAddons || [])
        .map((r) => ({ label: (r.label || "").trim(), cost: Number(r.cost || 0) }))
        .filter((r) => r.label && r.cost > 0);
      // Persist coat setup: the slot's applied coats (drives Scope PDF +
      // Work Order application steps) and the raw UI config for rehydration.
      body[`${field}_coats`] = (col.coats || [])
        .filter((c) => c.include)
        .map((c) => ({ name: c.name, passes: Number(c.passes) || 1, gps: Number(c.gps) || 0 }));
      body.calc_coat_config = coatConfig;
      // For Everest, persist the NDL UPGRADE delta in warranty_*_add (so the
      // PDF can render it as an optional add-on) — and remember the toggle
      // state in warranty_*_ndl so the calculator restores the same view.
      if ((col.isEverest || col.isGaf) && warField) body[warField] = Math.round((col.ndlUpgradeDelta || 0) * 100) / 100;
      if ((col.isEverest || col.isGaf) && ndlField) body[ndlField] = !!col.isNdl;
      // Hail Rider — Western Colloid 20/25-yr only. Round to dollars.
      // Always write (even 0) so a rep who explicitly cleared it removes
      // the line from the customer scope.
      if (col.hailRiderEligible && col.system.warranty_years === 25) {
        body.hail_rider_25yr_add = Math.round(col.hailRiderAmount || 0);
      }
      if (col.hailRiderEligible && col.system.warranty_years === 20) {
        body.hail_rider_20yr_add = Math.round(col.hailRiderAmount || 0);
      }
      // 💡 Persist material + subcontractor COST too — so the Live Project
      // P&L card shows this scope's real numbers the moment the option is
      // set, no second "Push Materials" click required. rawCost is
      // pre-markup; multiply by (1 + shipping%) so the P&L matches "material
      // price the deal actually has to spend to receive the goods".
      //
      // NOTE: SealTech's install crews are 1099 subcontractors, not W2 —
      // so the labor $ typed on the calc column is written to
      // `subcontractor_cost`. The `labor_cost` field is reserved for
      // future W2 employees and stays untouched here.
      const shipMult = 1 + Number(effMarkupPct || 0) / 100;
      body.materials_cost = Math.round(col.rawCost * shipMult * 100) / 100;
      body.subcontractor_cost = Math.round((col.laborAdd || 0) * 100) / 100;
      // Do NOT strip materials_cost / subcontractor_cost below — we want them persisted.
      ["id","created_at","updated_at","created_by",
       "labor_cost","other_expenses_total",
       "total_costs","profit","margin_pct","is_deleted","deleted_at","deleted_by",
       "assigned_user_name","primary_contact_name","property_name"
      ].forEach((k) => { delete body[k]; });
      const r = await api.put(`/deals/${deal.id}`, body);
      setDeal(r.data);
      toast.success(`Option ${letter} set: ${formatCurrency(baseForDeal)}. Live P&L updated (Materials ${formatCurrency(body.materials_cost)}${col.laborAdd > 0 ? ` · Subcontractor ${formatCurrency(body.subcontractor_cost)}` : ""}).`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSavingToDeal(false);
    }
  };

  /** ESTIMATE mode bulk: write ALL compared columns into A/B/C/D in one PUT. */
  const setAllOptionsOnDeal = async () => {
    if (!deal) { toast.warning("Pick a deal first"); return; }
    if (columns.length === 0) { toast.warning("Pick at least one system to compare"); return; }
    const updates = {};
    const summary = [];
    const LABOR_FIELDS = { 25: "labor_25yr_add", 20: "labor_20yr_add", 15: "labor_15yr_add", 10: "labor_10yr_add" };
    const OH_FIELDS    = { 25: "overhead_25yr_pct", 20: "overhead_20yr_pct", 15: "overhead_15yr_pct", 10: "overhead_10yr_pct" };
    const PR_FIELDS    = { 25: "profit_25yr_pct",   20: "profit_20yr_pct",   15: "profit_15yr_pct",   10: "profit_10yr_pct" };
    const WAR_FIELDS   = { 25: "warranty_25yr_add", 20: "warranty_20yr_add", 15: "warranty_15yr_add", 10: "warranty_10yr_add" };
    const NDL_FIELDS   = { 25: "warranty_25yr_ndl", 20: "warranty_20yr_ndl", 15: "warranty_15yr_ndl", 10: "warranty_10yr_ndl" };
    const claimed = {};
    for (const col of columns) {
      if (!activeVendors.has(col.system.vendor || "")) continue; // supplier-compare columns are price-check only
      const slot = slotForColumn(col, claimed);
      if (!slot) continue;
      claimed[slot.field] = col.system.id;
      const field = slot.field;
      const letter = slot.letter;
      updates[`${field}_system_id`] = col.system.id;
      updates[`${field}_label`] = slotLabel(col);
      updates[`${field}_warranty_add`] = slotWarrantyAdd(col);
      const brand = scopeManufacturerFor(col);
      if (brand) updates.scope_manufacturer = brand;
      // Everest jobs: save Standard-warranty base in proposal_option_* and
      // the marked-up NDL upgrade delta in warranty_*_add (the PDF will
      // render it as an "[OPTIONAL] Manufacturer Warranty" upgrade).
      // Custom Add-Ons are baked into the pushed price — they render as
      // price-hidden "included" bullets on the scope, so the headline
      // option $ must cover them (keeps scope == calculator).
      const baseForDeal = (col.isEverest || col.isGaf)
        ? col.customerBase + (col.customAddonTotal || 0)
        : col.customer;
      updates[field] = Math.round(baseForDeal * 100) / 100;
      const lf = LABOR_FIELDS[col.system.warranty_years];
      const of_ = OH_FIELDS[col.system.warranty_years];
      const pf = PR_FIELDS[col.system.warranty_years];
      const wf = WAR_FIELDS[col.system.warranty_years];
      const nf = NDL_FIELDS[col.system.warranty_years];
      if (lf) updates[lf] = Math.round((col.laborAdd || 0) * 100) / 100;
      if (of_) updates[of_] = col.ohPct;
      if (pf) updates[pf] = col.prPct;
      // Applied coats for this slot — drives Scope PDF + WO application steps.
      updates[`${field}_coats`] = (col.coats || [])
        .filter((c) => c.include)
        .map((c) => ({ name: c.name, passes: Number(c.passes) || 1, gps: Number(c.gps) || 0 }));
      if ((col.isEverest || col.isGaf) && wf) updates[wf] = Math.round((col.ndlUpgradeDelta || 0) * 100) / 100;
      if ((col.isEverest || col.isGaf) && nf) updates[nf] = !!col.isNdl;
      // Hail Rider — WC 20/25-yr only. Always writes so cleared values stick.
      if (col.hailRiderEligible && col.system.warranty_years === 25) {
        updates.hail_rider_25yr_add = Math.round(col.hailRiderAmount || 0);
      }
      if (col.hailRiderEligible && col.system.warranty_years === 20) {
        updates.hail_rider_20yr_add = Math.round(col.hailRiderAmount || 0);
      }
      summary.push(`Option ${letter} = ${formatCurrency(baseForDeal)}`);
    }
    if (!Object.keys(updates).length) {
      toast.warning("None of the picked systems have a matching A/B/C/D warranty (10/15/20/25-yr)");
      return;
    }
    // Auto-align the deal's proposed roof type to match the systems being
    // pushed. All bundled columns share the same vendor/system_type in
    // practice (a rep doesn't push Silicone + FARM in one shot), so we
    // key off the first column that yields a mapping.
    const chems = new Set(columns.filter((c) => activeVendors.has(c.system.vendor || ""))
      .map((c) => (/acrylic/i.test(c.system.system_type || "") ? "a" : /silicone/i.test(c.system.system_type || "") ? "s" : "")).filter(Boolean));
    const bulkRoofType = chems.size >= 2
      ? null // mixed silicone + acrylic — leave roof type alone; dynamic GAF table handles the scope
      : columns.filter((c) => activeVendors.has(c.system.vendor || "")).map((c) => roofTypeForSystem(c.system)).find(Boolean);
    if (bulkRoofType && deal.proposed_roof_type !== "Acrylic/Silicone") updates.proposed_roof_type = bulkRoofType;
    if (!window.confirm(`Set on "${deal.title || deal.id}":\n\n${summary.join("\n")}${bulkRoofType && bulkRoofType !== deal.proposed_roof_type ? `\n\nRoof type will change: ${deal.proposed_roof_type || "(unset)"} → ${bulkRoofType}` : ""}\n\nThis OVERWRITES any existing amounts in those fields.`)) return;
    setSavingToDeal(true);
    try {
      const body = { ...deal, ...updates };
      // Persist the SF typed in the Calculator so the Scope PDF's
      // "Install approximately N SF (S SQ)" line reflects the rep's number.
      const typedSf = Number(totalSf);
      if (typedSf > 0) body.total_sqft = typedSf;
      // Persist the rep's typed Custom Add-Ons alongside the per-option prices.
      body.calc_custom_addons = (customAddons || [])
        .map((r) => ({ label: (r.label || "").trim(), cost: Number(r.cost || 0) }))
        .filter((r) => r.label && r.cost > 0);
      // Raw coat UI config so reopening the calculator restores passes × GPS.
      body.calc_coat_config = coatConfig;
      ["id","created_at","updated_at","created_by",
       "materials_cost","labor_cost","subcontractor_cost","other_expenses_total",
       "total_costs","profit","margin_pct","is_deleted","deleted_at","deleted_by",
       "assigned_user_name","primary_contact_name","property_name"
      ].forEach((k) => { delete body[k]; });
      const r = await api.put(`/deals/${deal.id}`, body);
      setDeal(r.data);
      toast.success(`Set ${summary.length} proposal option${summary.length === 1 ? "" : "s"} on the deal.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSavingToDeal(false);
    }
  };

  /** Ensure a Vendor record exists for the picked manufacturer. Returns its id.
   *  Falls back to creating a minimal one (name only) — Darren can fill in
   *  contact + address via the Vendors page later. */
  const ensureVendorId = async (vendorName) => {
    try {
      const r = await api.get("/vendors");
      const existing = (r.data || []).find((v) => (v.name || "").toLowerCase() === vendorName.toLowerCase());
      if (existing) return existing.id;
      const created = await api.post("/vendors", { name: vendorName, vendor_type: "Material Supplier" });
      toast.info(`Created vendor record "${vendorName}" — fill in address/email in /vendors when you have a moment.`);
      return created.data.id;
    } catch (e) {
      toast.error(`Could not look up / create vendor: ${e.message}`);
      return null;
    }
  };

  /** MATERIALS mode: push the BoM to the deal as:
   *   1. material_takeoff lines (one per packed container, used by the PO PDF)
   *   2. cost_items lines (one per product, for cost tracking)
   *   3. winning_warranty_years (so reopening the calc auto-selects this system)
   *  Optionally chain into PO PDF download after the save succeeds.  */
  const pushMaterialsToDeal = async (col, { andDownloadPo = false } = {}) => {
    if (!deal) { toast.warning("Pick a deal first"); return; }
    const vendorName = col.system.vendor || selectedVendor;
    const vendorId = await ensureVendorId(vendorName);
    if (!vendorId) return;

    setSavingToDeal(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const newTakeoff  = [];
      const newCostItems = [];

      // Every line pushed by this click carries the same `source_key`.
      // Re-clicking Push Materials or Push + PO for the same
      // (vendor × system × warranty) combo is now idempotent — we strip
      // any lines with a matching source_key before appending the fresh
      // set. This fixes the doubling that happened when a rep clicked
      // Push Materials, then Push + PO, seeing quantities go from 5 → 10.
      const systemSlug = (col.system?.name || "system")
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const sourceKey = `calc:${vendorId}:${systemSlug}:${col.system?.warranty_years || 0}`;

      const addLines = (productOrAddon, packed, labelPrefix) => {
        for (const pk of packed) {
          const kind = classifyContainer(pk.product);
          const kindLabel = kind === "tote" ? "Tote" : kind === "drum" ? "Drum" : kind === "pail" ? "Pail" : (pk.product.unit || "");
          const unitDesc = `${pk.product.package_size} ${pk.product.unit} ${kindLabel}`.trim();
          // pk.cost is the RAW packed cost (catalog price × package × count).
          // Layer the shipping/tax markup on top so the Take-Off "Estimated"
          // reflects what the deal actually needs to spend to receive the
          // material — matches the standard "add 20% for shipping and taxes"
          // convention. Handling (~12%) is a separate later-stage line and
          // isn't baked in here.
          const shipMult = 1 + Number(effMarkupPct || 0) / 100;
          const rawUnit = pk.qty > 0 ? pk.cost / pk.qty : 0;
          const unitCost = Math.round(rawUnit * shipMult * 100) / 100;
          const lineTotal = Math.round(pk.cost * shipMult * 100) / 100;
          newTakeoff.push({
            id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
            source_key: sourceKey,
            vendor_id: vendorId,
            vendor_name: vendorName,
            sku: pk.product.sku || "",
            name: pk.product.name || labelPrefix,
            unit: unitDesc,
            quantity: pk.qty,
            unit_cost: unitCost,
            line_total: lineTotal,
            notes: `${labelPrefix} — ${col.system.name}`,
          });
        }
        if ((productOrAddon?.lineCost ?? 0) > 0) {
          const containerSummary = packed.map((x) => {
            const k = classifyContainer(x.product);
            const kl = k === "tote" ? "tote" : k === "drum" ? "drum" : k === "pail" ? "pail" : (x.product.unit || "");
            return `${x.qty}×${x.product.package_size}${x.product.unit} ${kl}`;
          }).join(", ");
          newCostItems.push({
            source_key: sourceKey,
            category: "Materials",
            vendor_id: vendorId,
            vendor_name: vendorName,
            description: `${labelPrefix} (${containerSummary}) — ${col.system.name}`,
            amount: Math.round(productOrAddon.lineCost * 100) / 100,
            date: today,
            status: "Pending",
          });
        }
      };

      for (const ln of col.lines) {
        if (ln.lineCost <= 0) continue;
        const base = (ln.product.name || "").split(" — ")[0];
        addLines(ln, ln.packed, base);
      }
      for (const ln of col.addonLines) {
        if (ln.lineCost <= 0) continue;
        addLines(ln, ln.packed, ln.addon.label);
      }

      // Sub-crew cost — push the rep-entered per-column labor $ as a
      // Subcontractor cost_item (SealTech's install crews are 1099 subs,
      // not W2). The `Labor` category on the Live P&L stays available for
      // future W2 employees.
      if (Number(col.laborAdd) > 0) {
        newCostItems.push({
          source_key: sourceKey,
          category: "Subcontractor",
          vendor_id: null,
          vendor_name: "1099 Install Crew",
          description: `Sub-crew estimate — ${col.system.name} (${col.system.warranty_years}-yr) — from Calculator`,
          amount: Math.round(Number(col.laborAdd) * 100) / 100,
          date: today,
          status: "Pending",
        });
      }

      // Strip existing lines that this same click already pushed before
      // (identified by matching `source_key`), so re-clicking Push Materials
      // or Push + PO for the same vendor × system combo is idempotent.
      // Pre-Feb-26-2026 lines have no source_key so they're never stripped
      // — that intentionally preserves any manually-typed rows.
      const keptTakeoff = (deal.material_takeoff || []).filter((ln) => ln.source_key !== sourceKey);
      const keptCosts   = (deal.cost_items      || []).filter((ln) => ln.source_key !== sourceKey);
      const replacedTakeoff = (deal.material_takeoff || []).length - keptTakeoff.length;

      const body = {
        ...deal,
        material_takeoff: [...keptTakeoff, ...newTakeoff],
        cost_items:       [...keptCosts,   ...newCostItems],
        winning_warranty_years: col.system.warranty_years || null,
        winning_system_name: col.system.name || "",
      };
      // Also persist the rep-typed SF so downstream POs / scope reflect
      // the same square footage the calc used.
      const typedSf = Number(totalSf);
      if (typedSf > 0) body.total_sqft = typedSf;
      ["id","created_at","updated_at","created_by",
       "materials_cost","labor_cost","subcontractor_cost","other_expenses_total",
       "total_costs","profit","margin_pct","is_deleted","deleted_at","deleted_by",
       "assigned_user_name","primary_contact_name","property_name"
      ].forEach((k) => { delete body[k]; });
      const r = await api.put(`/deals/${deal.id}`, body);
      setDeal(r.data);

      toast.success(`${replacedTakeoff > 0 ? `Replaced ${replacedTakeoff} prior lines · ` : ""}Pushed ${newTakeoff.length} take-off line${newTakeoff.length === 1 ? "" : "s"} + ${newCostItems.length} cost line${newCostItems.length === 1 ? "" : "s"} for ${col.system.name}${col.laborAdd > 0 ? " (materials + 1099 sub)" : ""}.`);

      if (andDownloadPo) {
        // Open the existing PO PDF endpoint in a new tab. The endpoint reads
        // the auth token from `?token=` query param (it's a GET that the
        // browser opens directly, so the Authorization header isn't sent).
        const token = localStorage.getItem("crm_token") || "";
        const base = process.env.REACT_APP_BACKEND_URL;
        window.open(`${base}/api/deals/${deal.id}/purchase-order/${vendorId}.pdf?token=${encodeURIComponent(token)}`, "_blank");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSavingToDeal(false);
    }
  };

  /** MATERIALS mode: generate PO from EXISTING material_takeoff on the deal
   *  (i.e. user already pushed materials earlier and just wants the PDF). */
  const downloadPoOnly = async () => {
    if (!deal) return;
    const vendorId = await ensureVendorId(selectedVendor);
    if (!vendorId) return;
    setGeneratingPo(true);
    try {
      const hasLines = (deal.material_takeoff || []).some((ln) => ln.vendor_id === vendorId);
      if (!hasLines) {
        toast.error(`No take-off lines for ${selectedVendor} on this deal yet. Click "Push to Materials List" first.`);
        return;
      }
      const token = localStorage.getItem("crm_token") || "";
      const base = process.env.REACT_APP_BACKEND_URL;
      window.open(`${base}/api/deals/${deal.id}/purchase-order/${vendorId}.pdf?token=${encodeURIComponent(token)}`, "_blank");
    } finally {
      setGeneratingPo(false);
    }
  };

  /** Unique vendor list for the manufacturer dropdown. */
  const vendorOptions = useMemo(() => {
    const set = new Set();
    for (const s of systems) if (s.vendor) set.add(s.vendor);
    return Array.from(set).sort();
  }, [systems]);

  /** Filtered systems list for the currently-picked manufacturer.
   *  Honors the admin-set `sort_order` field first (so Darren can pin the
   *  exact left-rail sequence in the DB), then falls back to warranty DESC
   *  for any system that hasn't been assigned an order yet. */
  const filteredSystems = useMemo(() => {
    return systems
      .filter((s) => (s.vendor || "Unknown") === selectedVendor)
      .sort((a, b) => {
        const ao = (a.sort_order ?? 9999);
        const bo = (b.sort_order ?? 9999);
        if (ao !== bo) return ao - bo;
        return (b.warranty_years || 0) - (a.warranty_years || 0);
      });
  }, [systems, selectedVendor]);

  // GAF-family vendors (Pacific Coast + ABC Supply) share the SAME optional
  // add-on rail — ABC sells GAF products so fabrics/tapes/caps are identical.
  const isGafAddonVendor = (selectedVendor || "").toLowerCase().includes("gaf")
    || (selectedVendor || "").toLowerCase().includes("abc supply");

  /** Group a vendor's systems into families — "20-Year GAF High Solids
   *  Silicone System" and "10-Year GAF High Solids Silicone System" collapse
   *  into ONE row ("GAF High Solids Silicone System") with a warranty
   *  dropdown. Family = system name minus the leading "NN-Year " prefix. */
  const familyOf = (name) => (name || "").replace(/^\d+\s*-?\s*Year\s+/i, "").trim();
  const groupFamilies = (list) => {
    const map = new Map();
    list.forEach((s, idx) => {
      const fam = familyOf(s.name);
      if (!map.has(fam)) map.set(fam, { label: fam, order: idx, tiers: [] });
      map.get(fam).tiers.push(s);
    });
    const fams = Array.from(map.values());
    fams.forEach((f) => f.tiers.sort((a, b) => (b.warranty_years || 0) - (a.warranty_years || 0)));
    return fams.sort((a, b) => a.order - b.order);
  };
  const systemFamilies = useMemo(() => groupFamilies(filteredSystems), [filteredSystems]);

  // Two-manufacturer scope building — the optional Manufacturer 2 dropdown
  // adds a second systems rail whose columns are FULLY pushable (unlike the
  // price-check-only Supplier Compare rail).
  const activeVendors = useMemo(
    () => new Set([selectedVendor, secondVendor].filter(Boolean)),
    [selectedVendor, secondVendor]
  );
  const secondFamilies = useMemo(() => {
    if (!secondVendor) return [];
    const list = systems
      .filter((s) => (s.vendor || "Unknown") === secondVendor)
      .sort((a, b) => {
        const ao = (a.sort_order ?? 9999);
        const bo = (b.sort_order ?? 9999);
        if (ao !== bo) return ao - bo;
        return (b.warranty_years || 0) - (a.warranty_years || 0);
      });
    return groupFamilies(list);
  }, [systems, secondVendor]);

  // Supplier Compare — Pacific and ABC both sell GAF systems. When one is the
  // active manufacturer, surface the OTHER supplier's families in a second
  // rail so the rep can price the identical job under both and spot the
  // cheaper supplier. Cross-supplier columns are price-check only (push
  // buttons stay tied to the active Manufacturer).
  const counterpartVendor = useMemo(() => {
    if (!isGafAddonVendor) return null;
    const low = (v) => (v || "").toLowerCase();
    return vendorOptions.find((v) => v !== selectedVendor && v !== secondVendor && (low(v).includes("gaf") || low(v).includes("abc supply"))) || null;
  }, [vendorOptions, selectedVendor, secondVendor, isGafAddonVendor]);
  const counterpartFamilies = useMemo(() => {
    if (!counterpartVendor) return [];
    const list = systems
      .filter((s) => (s.vendor || "Unknown") === counterpartVendor)
      .sort((a, b) => {
        const ao = (a.sort_order ?? 9999);
        const bo = (b.sort_order ?? 9999);
        if (ao !== bo) return ao - bo;
        return (b.warranty_years || 0) - (a.warranty_years || 0);
      });
    return groupFamilies(list);
  }, [systems, counterpartVendor]);

  /** Gallon coating lines of a system's recipe (base coat, top coat...). */
  const galLinesOf = (sysId) => (recipes[sysId] || []).filter((r) => {
    const p = productById[r.product_id];
    return r.coverage_basis === "per_100sf" && p?.unit === "gal";
  });

  const coatLabel = (r) => {
    const name = `${productById[r.product_id]?.name || ""} ${r.notes || ""}`;
    if (/base/i.test(name)) return "Base Coat";
    if (/top/i.test(name)) return "Top Coat";
    return (productById[r.product_id]?.name || "Coat").slice(0, 22);
  };

  /** Family-row selection: swap which warranty tier of this family is in the
   *  compare grid (at most one tier per family). Empty sysId = deselect. */
  const selectTier = async (fam, sysId) => {
    const famIds = fam.tiers.map((t) => t.id);
    const kept = selectedSystemIds.filter((id) => !famIds.includes(id));
    if (!sysId) { setSelectedSystemIds(kept); return; }
    if (kept.length >= MAX_COMPARE) {
      toast.warning(`Compare up to ${MAX_COMPARE} systems at a time`);
      return;
    }
    setSelectedSystemIds([...kept, sysId]);
    if (!recipes[sysId]) {
      try {
        const r = await api.get(`/systems/${sysId}/recipe`);
        setRecipes((prev) => ({ ...prev, [sysId]: r.data || [] }));
      } catch (e) {
        toast.error("Could not load recipe");
      }
    }
  };

  // Auto-select the deal's "winning system" so the rep doesn't have to
  // re-pick the same warranty band they already priced when building the
  // scope. Signal-priority (best → worst):
  //   1. `winning_warranty_years` — set the last time materials were pushed
  //   2. `chosen_option_id` — set once the customer signs the proposal
  //   3. `chosen_amount` fuzzy-matched against the four `proposal_option_*`
  //      slots (within 20%, accounts for NDL / hail-rider upgrades)
  // Only auto-picks when the compare grid is empty AND the deal has a signal
  // (never overrides the rep's manual pick, never fights their vendor swap).
  useEffect(() => {
    if (!deal || filteredSystems.length === 0 || selectedSystemIds.length > 0) return;

    const OPTION_ID_TO_WY = { opt_25: 25, opt_20: 20, opt_15: 15, opt_10: 10 };
    const PROPOSAL_FIELDS = [
      { field: "proposal_option_25yr", wy: 25 },
      { field: "proposal_option_1",    wy: 20 },
      { field: "proposal_option_2",    wy: 15 },
      { field: "proposal_option_3",    wy: 10 },
    ];

    let targetWy = null;

    if (deal.winning_warranty_years) {
      targetWy = Number(deal.winning_warranty_years);
    } else if (deal.chosen_option_id && OPTION_ID_TO_WY[deal.chosen_option_id]) {
      targetWy = OPTION_ID_TO_WY[deal.chosen_option_id];
    } else if (Number(deal.chosen_amount) > 0) {
      const chosen = Number(deal.chosen_amount);
      let best = null;
      for (const { field, wy } of PROPOSAL_FIELDS) {
        const price = Number(deal[field] || 0);
        if (price <= 0) continue;
        const diffPct = Math.abs(price - chosen) / chosen;
        if (best === null || diffPct < best.diffPct) best = { wy, diffPct };
      }
      // 20% tolerance covers hail-rider / NDL upgrades that inflate the
      // signed total above the raw proposal-option row.
      if (best && best.diffPct <= 0.20) targetWy = best.wy;
    }

    if (!targetWy) return;
    const sys = filteredSystems.find((s) => s.warranty_years === targetWy);
    if (sys) toggleSystem(sys.id);
  }, [deal?.id, filteredSystems]);

  /** Shared family-row renderer — used by the main systems rail AND the
   *  Supplier Compare rail. Warranty dropdown + per-coat passes × GPS editor. */
  const renderFamilyRow = (fam) => {
    const slug = fam.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const selected = fam.tiers.find((t) => selectedSystemIds.includes(t.id));
    const coatRows = selected ? galLinesOf(selected.id) : [];
    const sysTotal = selected
      ? coatRows.reduce((acc, r) => {
          const cfg = coatCfg(selected, r);
          return acc + (cfg.include ? (Number(cfg.passes) || 1) * (Number(cfg.gps) || 0) : 0);
        }, 0)
      : 0;
    const patchCoat = (r, patch) => {
      const key = `${selected.id}:${r.id}`;
      setCoatConfig((prev) => ({ ...prev, [key]: { ...coatCfg(selected, r), ...patch } }));
    };
    return (
      <div
        key={fam.label}
        className={`px-3 py-2.5 border-b border-zinc-100 last:border-b-0 ${selected ? "bg-blue-50" : ""}`}
        data-testid={`system-family-${slug}`}
      >
        <div className="text-xs font-bold">{fam.label}</div>
        <div className="mt-1.5">
          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mb-0.5">Warranty</div>
          <select
            value={selected?.id || ""}
            onChange={(e) => selectTier(fam, e.target.value)}
            data-testid={`family-warranty-${slug}`}
            className="border border-zinc-300 px-1.5 h-8 text-xs w-full bg-white focus:outline-none focus:border-blue-700"
          >
            <option value="">—</option>
            {fam.tiers.map((t) => (
              <option key={t.id} value={t.id}>{bandWarrantyLabel(t.warranty_years)}</option>
            ))}
          </select>
        </div>
        {!selected && (
          <div className="text-[9px] text-zinc-400 mt-1 italic">Pick a warranty to set coats — passes × GPS per coat</div>
        )}
        {selected && coatRows.length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid={`coat-editor-${slug}`}>
            <div className="grid grid-cols-[1fr_34px_10px_44px_38px] gap-1 items-center text-[9px] font-bold uppercase tracking-widest text-zinc-400">
              <span>Coat</span><span className="text-center">Passes</span><span></span><span className="text-center">GPS</span><span className="text-right">= Tot</span>
            </div>
            {coatRows.map((r, i) => {
              const cfg = coatCfg(selected, r);
              const lineTotal = cfg.include ? (Number(cfg.passes) || 1) * (Number(cfg.gps) || 0) : 0;
              return (
                <div key={r.id} className="grid grid-cols-[1fr_34px_10px_44px_38px] gap-1 items-center">
                  <label className="flex items-center gap-1 text-[11px] font-bold text-zinc-700 cursor-pointer min-w-0">
                    <input
                      type="checkbox"
                      checked={cfg.include}
                      onChange={(e) => patchCoat(r, { include: e.target.checked })}
                      data-testid={`coat-toggle-${slug}-${i}`}
                    />
                    <span className="truncate">{coatLabel(r)}</span>
                  </label>
                  <input
                    type="number" min="1" step="1"
                    value={cfg.passes}
                    disabled={!cfg.include}
                    onChange={(e) => patchCoat(r, { passes: Math.max(1, Number(e.target.value) || 1) })}
                    data-testid={`coat-passes-${slug}-${i}`}
                    className="border border-zinc-300 px-1 h-7 text-[11px] font-mono text-center w-full focus:outline-none focus:border-blue-700 disabled:bg-zinc-100 disabled:text-zinc-400"
                  />
                  <span className="text-[10px] text-zinc-400 text-center">×</span>
                  <input
                    type="number" min="0" step="0.25"
                    value={cfg.gps}
                    disabled={!cfg.include}
                    onChange={(e) => patchCoat(r, { gps: Math.max(0, Number(e.target.value) || 0) })}
                    data-testid={`coat-gps-${slug}-${i}`}
                    className="border border-zinc-300 px-1 h-7 text-[11px] font-mono text-center w-full focus:outline-none focus:border-blue-700 disabled:bg-zinc-100 disabled:text-zinc-400"
                  />
                  <span className="text-[11px] font-mono font-bold text-right">{lineTotal > 0 ? `${Math.round(lineTotal * 100) / 100}` : "—"}</span>
                </div>
              );
            })}
            <div className="text-[10px] font-bold text-right text-blue-800" data-testid={`coat-total-${slug}`}>
              System total: {Math.round(sysTotal * 100) / 100} Gal/SQ
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="p-10 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading catalog…</div>;
  }

  return (
    <div className="max-w-[1500px] mx-auto p-6 space-y-6" data-testid="calculator-page">
      {!deal && <ScopeLaneSwitcher active="fluid" />}
      {!deal && <RecentScopes />}
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
            <CalcIcon className="w-3.5 h-3.5" /> Build Scope
          </div>
          <h1 className="text-2xl font-black tracking-tight mt-1">
            {deal ? `Estimate for ${deal.title || deal.name || "Deal"}` : "Quick System Compare"}
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Pick up to {MAX_COMPARE} systems, enter total square footage, and see a side-by-side material list with the customer-facing price (cost + {settings.markup_pct}% shipping + {settings.handling_pct}% handling).
          </p>
        </div>
        {deal && (
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Link to={`/deals/${deal.id}`} className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 rounded-sm" data-testid="back-to-deal">
                <ChevronLeft className="w-3 h-3" /> Back to Deal
              </Link>
              <button
                type="button"
                onClick={() => {
                  const token = localStorage.getItem("crm_token") || "";
                  const base = process.env.REACT_APP_BACKEND_URL;
                  window.open(`${base}/api/deals/${deal.id}/spec-sheet.pdf?token=${encodeURIComponent(token)}`, "_blank");
                }}
                className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm"
                data-testid="download-scope-pdf"
                title="Download the scope PDF straight to your device — for in-person customer meetings."
              >
                <Download className="w-3 h-3" /> Download Scope
              </button>
              <Link
                to={`/deals/${deal.id}?openScope=1`}
                className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-wider bg-amber-600 text-white hover:bg-amber-700 rounded-sm"
                data-testid="open-scope"
                title="Jump to the scope editor on the deal so you can finalize the proposal text and email it from there."
              >
                <FileText className="w-3 h-3" /> Edit Scope →
              </Link>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const r = await api.get(`/deals/${deal.id}/sign-link`);
                    const url = r.data?.sign_url;
                    if (!url) { toast.error("Could not get sign link"); return; }
                    // Copy to clipboard
                    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
                    // Open in a new tab so Darren can hand the device to the customer
                    window.open(url, "_blank");
                    toast.success(r.data?.already_signed
                      ? "This deal is already signed — opened the receipt view."
                      : "Sign link copied to clipboard AND opened in a new tab — hand it to the customer.");
                  } catch (e) {
                    toast.error(formatApiError(e?.response?.data?.detail) || e.message);
                  }
                }}
                className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-wider bg-emerald-700 text-white hover:bg-emerald-800 rounded-sm"
                data-testid="get-signed"
                title="Mints (or reuses) the public sign URL, copies it to your clipboard, and opens it in a new tab so you can hand the device to the customer right at the table."
              >
                <PenLine className="w-3 h-3" /> Get Signed →
              </button>
            </div>
            {/* Mode toggle removed — mode auto-switches based on
                deal.scope_signed_at (see the effect at ~line 333). Pre-signature
                the app is in Estimate mode; once the customer signs it flips
                to Materials & PO. Buttons on the deal header (Edit Scope, Get
                Signed, Download Scope) cover the pre-sign flow. */}
            {deal.scope_signed_at && (
              <div className="text-[10px] text-emerald-700 font-mono">Scope signed {String(deal.scope_signed_at).slice(0, 10)}</div>
            )}
          </div>
        )}
      </header>

      {/* Working-On sticky context banner. Only renders when a deal is
          picked. Sits just below the page header and sticks to the top
          on scroll so the rep never loses track of which deal they're
          building an estimate for. Everything is one row on desktop and
          wraps naturally on mobile. */}
      {deal && (
        <div
          className="sticky top-0 z-30 -mx-6 px-6 py-3 bg-blue-900 text-white shadow-md flex flex-wrap items-center gap-x-4 gap-y-1.5"
          data-testid="working-on-banner"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-blue-300 flex-shrink-0">Working On</div>
            <Link
              to={`/projects/${deal.id}`}
              className="font-heading text-lg font-black tracking-tight truncate hover:text-blue-200 transition-colors"
              title="Jump back to the deal"
              data-testid="working-on-deal-title"
            >
              {deal.title || "Untitled Deal"}
            </Link>
            {deal.status && (
              <span className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-blue-700 text-white rounded-sm flex-shrink-0">
                {deal.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-blue-200 flex-wrap">
            {property?.address_line_1 && (
              <span className="inline-flex items-center gap-1" title="Site address">
                <span className="text-blue-300">📍</span>
                <span className="truncate max-w-[280px]">{[property.address_line_1, property.city, property.state].filter(Boolean).join(", ")}</span>
              </span>
            )}
            {deal.proposed_roof_type && (
              <span className="inline-flex items-center gap-1" title="Proposed roof type">
                <span className="text-blue-300">Roof:</span>
                <span className="font-bold text-white">{deal.proposed_roof_type}</span>
              </span>
            )}
            {totalSf && (
              <span className="inline-flex items-center gap-1" title="Total roof SF">
                <span className="text-blue-300">SF:</span>
                <span className="font-mono font-bold text-white">{Number(totalSf).toLocaleString()}</span>
              </span>
            )}
            {deal.chosen_amount ? (
              <span className="inline-flex items-center gap-1" title="Contract amount">
                <span className="text-blue-300">Contract:</span>
                <span className="font-mono font-bold text-emerald-300">${Number(deal.chosen_amount).toLocaleString()}</span>
              </span>
            ) : null}
          </div>
        </div>
      )}

      {/* Manufacturer + Deal selector strip (the "pre-flight" choices). */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-zinc-50 border-2 border-zinc-300 rounded-sm">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Manufacturer</label>
          <select
            value={selectedVendor}
            onChange={(e) => {
              setSelectedVendor(e.target.value);
              if (secondVendor === e.target.value) setSecondVendor("");
              setSelectedSystemIds([]);   // clear comparisons — they were from the old vendor
            }}
            data-testid="select-vendor"
            className="border border-zinc-300 px-3 h-10 text-base w-full bg-white focus:outline-none focus:border-blue-700"
          >
            {vendorOptions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="text-[10px] text-zinc-500 mt-1">Primary manufacturer — drives the add-on rail and PO PDF.</div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Manufacturer 2 (optional)</label>
          <select
            value={secondVendor}
            onChange={(e) => {
              const v = e.target.value;
              setSecondVendor(v);
              // Drop selections that belong to neither active manufacturer.
              setSelectedSystemIds((prev) => prev.filter((id) => {
                const s = systems.find((x) => x.id === id);
                const sv = s?.vendor || "";
                return sv === selectedVendor || (v && sv === v);
              }));
            }}
            data-testid="select-second-vendor"
            className="border border-zinc-300 px-3 h-10 text-base w-full bg-white focus:outline-none focus:border-blue-700"
          >
            <option value="">— None —</option>
            {vendorOptions.filter((v) => v !== selectedVendor).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="text-[10px] text-zinc-500 mt-1">Pull systems from a second manufacturer onto the same scope.</div>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Working on Deal</label>
          <select
            value={selectedDealId}
            onChange={(e) => setSelectedDealId(e.target.value)}
            data-testid="select-deal"
            className="border border-zinc-300 px-3 h-10 text-base w-full bg-white focus:outline-none focus:border-blue-700"
          >
            <option value="">— No deal (comparison only) —</option>
            {deals.map((d) => {
              const label = `${d.title || d.name || "(untitled)"}${d.stage ? ` · ${d.stage}` : ""}`;
              return <option key={d.id} value={d.id}>{label.slice(0, 80)}</option>;
            })}
          </select>
          <div className="text-[10px] text-zinc-500 mt-1">Sorted by most-recently-updated. Pushing a column sends cost lines + auto-fills the matching proposal option.</div>
        </div>
      </div>

      {/* Controls bar */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 p-4 bg-white border border-zinc-200 rounded-sm shadow-sm">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Total Roof SF</label>
          <input
            type="number" min="0" inputMode="numeric"
            value={totalSf} onChange={(e) => setTotalSf(e.target.value)}
            placeholder="e.g. 12,500"
            data-testid="input-total-sf"
            className="border border-zinc-300 px-3 h-10 text-base w-full font-mono focus:outline-none focus:border-blue-700"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Waste %</label>
          <input
            type="number" min="0" max="100" step="0.5"
            value={waste} onChange={(e) => setWaste(Number(e.target.value))}
            data-testid="input-waste"
            className="border border-zinc-300 px-3 h-10 text-base w-full font-mono focus:outline-none focus:border-blue-700"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1" title="Uncheck a container size if the site can't physically accept it (no forklift, narrow access, etc.)">
            Site Access — Allowed Containers
          </label>
          <div className="flex items-center gap-2 h-10">
            {[
              { key: "tote", label: "Totes",  hint: "275 gal" },
              { key: "drum", label: "Drums",  hint: "55 gal"  },
              { key: "pail", label: "Pails",  hint: "5 gal"   },
            ].map((c) => {
              const on = allowedSizes[c.key];
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setAllowedSizes({ ...allowedSizes, [c.key]: !on })}
                  data-testid={`toggle-container-${c.key}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-10 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                    on
                      ? "bg-blue-700 text-white border-blue-700 hover:bg-blue-800"
                      : "bg-zinc-100 text-zinc-400 border-zinc-300 hover:bg-zinc-200 line-through"
                  }`}
                  title={on ? `${c.label} (${c.hint}) included` : `${c.label} (${c.hint}) excluded — site can't accept`}
                >
                  {c.label}
                  <span className={`text-[9px] font-mono ${on ? "text-blue-100" : "text-zinc-400"}`}>{c.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="text-xs flex flex-col justify-center">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Shipping %</div>
          <div className="flex items-center gap-1.5">
            <input
              type="number" min="0" max="100" step="0.5"
              value={shippingPctOv}
              onChange={(e) => setShippingPctOv(e.target.value)}
              placeholder={String(settings.markup_pct)}
              data-testid="input-shipping-pct"
              className="w-16 border border-zinc-300 px-2 h-8 text-xs font-mono focus:outline-none focus:border-blue-700"
              title={`Shipping % on raw materials — default ${settings.markup_pct}%. Clear to reset.`}
            />
            <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">on raw{shippingPctOv !== "" && Number(shippingPctOv) !== Number(settings.markup_pct) ? ` (default ${settings.markup_pct}%)` : ""}</span>
          </div>
        </div>
        <div className="text-xs flex flex-col justify-center">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Handling Fee %</div>
          <div className="flex items-center gap-1.5">
            <input
              type="number" min="0" max="100" step="0.5"
              value={handlingPctOv}
              onChange={(e) => setHandlingPctOv(e.target.value)}
              placeholder={String(settings.handling_pct)}
              data-testid="input-handling-pct"
              className="w-16 border border-zinc-300 px-2 h-8 text-xs font-mono focus:outline-none focus:border-blue-700"
              title={`Handling % — default ${settings.handling_pct}%. Clear to reset.`}
            />
            <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
              {settings.handling_basis === "marked_up" ? "on Tax & Delivery total" : "on raw cost"}{handlingPctOv !== "" && Number(handlingPctOv) !== Number(settings.handling_pct) ? ` (default ${settings.handling_pct}%)` : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Systems sidebar — flat list of the chosen manufacturer's systems */}
        <aside className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-sm shadow-sm">
            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 flex items-center gap-1.5">
              <Layers className="w-3 h-3" /> {/systems\s*$/i.test(selectedVendor) ? selectedVendor : `${selectedVendor} Systems`} ({selectedSystemIds.length}/{MAX_COMPARE})
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {systemFamilies.length === 0 && (
                <div className="p-4 text-sm text-zinc-500">No systems for {selectedVendor} yet — add one in <Link to="/catalog" className="text-blue-700 underline">Product Catalog</Link>.</div>
              )}
              {systemFamilies.map(renderFamilyRow)}
            </div>
          </div>

          {/* Manufacturer 2 systems — fully pushable, same as the primary rail */}
          {secondVendor && (
            <div className="bg-white border border-blue-300 rounded-sm shadow-sm" data-testid="second-vendor-rail">
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-blue-700 border-b border-blue-200 bg-blue-50 flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> {/systems\s*$/i.test(secondVendor) ? secondVendor : `${secondVendor} Systems`}
              </div>
              <div className="max-h-[40vh] overflow-y-auto">
                {secondFamilies.length === 0 ? (
                  <div className="p-4 text-sm text-zinc-500">No systems for {secondVendor} yet.</div>
                ) : secondFamilies.map(renderFamilyRow)}
              </div>
            </div>
          )}

          {/* Supplier Compare — the other GAF supplier's systems, price-check only */}
          {counterpartVendor && counterpartFamilies.length > 0 && (
            <div className="bg-white border border-amber-400 rounded-sm shadow-sm" data-testid="supplier-compare-rail">
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-amber-700 border-b border-amber-200 bg-amber-50 flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Supplier Compare — {counterpartVendor}
              </div>
              <div className="max-h-[40vh] overflow-y-auto">
                {counterpartFamilies.map(renderFamilyRow)}
              </div>
              <div className="px-3 py-2 text-[10px] text-zinc-500 italic border-t border-zinc-100">
                Price the same job under {counterpartVendor} to spot the cheaper supplier. These columns are price-check only — switch Manufacturer above to push one to the deal.
              </div>
            </div>
          )}

          {/* Add-ons — one section per ACTIVE manufacturer family so mixed
              jobs (e.g. GAF + Western Colloid) get both brands' add-ons. */}
          {(() => {
            const addonSections = [];
            const seen = new Set();
            for (const v of [selectedVendor, secondVendor].filter(Boolean)) {
              const fam = vendorFamily(v);
              if (seen.has(fam)) continue;
              seen.add(fam);
              addonSections.push({ fam, vendor: v, templates: TEMPLATES_BY_FAMILY[fam] });
            }
            return (
          <div className="bg-white border border-zinc-200 rounded-sm shadow-sm">
            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 flex items-center justify-between">
              <span>Optional Add-ons</span>
              {addonSections.some((s) => s.fam === "wc") && (
                <button
                  type="button"
                  onClick={() => setAddons({ ...addons, ...STRESS_POINTS_PRESET })}
                  data-testid="apply-stress-points-preset"
                  className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:underline"
                  title="Drops 1×4&quot;, 2×6&quot;, 1×12&quot; reinforcing fabric in the add-on row — typical stress-point bundle for a small commercial roof."
                >
                  + Stress Points
                </button>
              )}
            </div>
            <div className="p-3 space-y-2.5 max-h-[55vh] overflow-y-auto">
              {addonSections.map((sec) => (
                <div key={sec.fam} className="space-y-2.5" data-testid={`addon-section-${sec.fam}`}>
                  {addonSections.length > 1 && (
                    <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700 pt-1 border-t first:border-t-0 border-zinc-100">{sec.vendor}</div>
                  )}
                  {sec.templates.map((a) => (
                    <div key={a.id} className="grid grid-cols-[1fr_70px] gap-2 items-center">
                      <span className="text-xs">{a.label}</span>
                      <input
                        type="number" min="0" step="1"
                        value={addons[a.id] || ""}
                        onChange={(e) => setAddons({ ...addons, [a.id]: Number(e.target.value) || 0 })}
                        placeholder={a.unit}
                        data-testid={`addon-${a.id}`}
                        className="border border-zinc-300 px-2 h-8 text-xs font-mono focus:outline-none focus:border-blue-700"
                      />
                    </div>
                  ))}
                  {sec.fam === "everest" && (
                    <div className="pt-2 text-[10px] text-zinc-500 italic border-t border-zinc-100">
                      Adding any granule qty auto-applies the SESCO LTL freight ($2,000 flat per order).
                    </div>
                  )}
                </div>
              ))}

              {/* Free-form custom add-on rows (available on every vendor —
                  Western Colloid AND Everest/Silicone). Each row is label +
                  $ amount, added flat to every compared system's customer
                  price. Useful for one-off items (e.g. "Skylight curb
                  flashing — $850"). Empty rows are ignored. */}
              <div className="pt-3 border-t border-dashed border-zinc-300 space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Custom Add-Ons
                  </div>
                  {customAddons.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_90px] gap-2 items-center">
                      <input
                        type="text"
                        value={row.label}
                        onChange={(e) => {
                          const next = [...customAddons];
                          next[idx] = { ...next[idx], label: e.target.value };
                          setCustomAddons(next);
                        }}
                        placeholder={`Add-on #${idx + 1} description`}
                        data-testid={`custom-addon-label-${idx}`}
                        className="border border-zinc-300 px-2 h-8 text-xs focus:outline-none focus:border-blue-700"
                      />
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={row.cost}
                          onChange={(e) => {
                            const next = [...customAddons];
                            next[idx] = { ...next[idx], cost: e.target.value };
                            setCustomAddons(next);
                          }}
                          placeholder="0.00"
                          data-testid={`custom-addon-cost-${idx}`}
                          className="border border-zinc-300 pl-5 pr-2 h-8 w-full text-xs font-mono focus:outline-none focus:border-blue-700"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="text-[10px] text-zinc-500 italic leading-snug">
                    Customer-facing dollars (no OH/Profit applied). Added flat to every system column. Leave blank to skip.
                  </div>
              </div>
            </div>
          </div>
            );
          })()}
        </aside>

        {/* Comparison columns */}
        <section>
          {selectedSystemIds.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-300 p-12 text-center rounded-sm">
              <AlertCircle className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
              <div className="text-sm font-bold uppercase tracking-wider text-zinc-600">Pick a system to begin</div>
              <p className="text-xs text-zinc-500 mt-1">Choose from the sidebar — you can compare up to {MAX_COMPARE} side-by-side.</p>
            </div>
          ) : !Number(totalSf) ? (
            <div className="border-2 border-dashed border-zinc-300 p-12 text-center rounded-sm">
              <AlertCircle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <div className="text-sm font-bold uppercase tracking-wider text-zinc-600">Enter the roof&apos;s total square footage</div>
              <p className="text-xs text-zinc-500 mt-1">Quantities are computed from coverage rates × SF.</p>
            </div>
          ) : (
            <>
            <div className={`grid gap-4 ${columns.length === 1 ? "grid-cols-1" : columns.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}>
              {columns.map((col, idx) => {
                // Detect "typed but not saved" for the labor/OH/profit fields
                // on this column's warranty year. `_add` fields default to 0
                // and `_pct` fields default to undefined on the deal, so we
                // only complain when there's a meaningful delta.
                const y = col.system.warranty_years;
                const sameVendor = activeVendors.has(col.system.vendor || "");
                const persisted = {
                  labor:    Number(deal?.[`labor_${y}yr_add`] ?? 0),
                  overhead: deal?.[`overhead_${y}yr_pct`],
                  profit:   deal?.[`profit_${y}yr_pct`],
                };
                const unsavedFields = [];
                if (deal && Number(col.laborAdd || 0) !== persisted.labor) unsavedFields.push("Labor");
                const ohOv = overheadBySystem[col.system.id];
                const prOv = profitBySystem[col.system.id];
                if (deal && ohOv !== undefined && Number(ohOv) !== Number(persisted.overhead ?? ohOv)) unsavedFields.push("Overhead %");
                if (deal && prOv !== undefined && Number(prOv) !== Number(persisted.profit ?? prOv)) unsavedFields.push("Profit %");
                return (
                <CompareColumn
                  key={col.system.id}
                  col={col}
                  settings={settings}
                  totalSf={Number(totalSf)}
                  mode={mode}
                  onRemove={() => toggleSystem(col.system.id)}
                  crossVendor={!sameVendor}
                  onSetOption={deal && sameVendor ? () => setOptionOnDeal(col) : null}
                  onPushMaterials={deal && sameVendor ? () => pushMaterialsToDeal(col) : null}
                  onPushAndPo={deal && sameVendor ? () => pushMaterialsToDeal(col, { andDownloadPo: true }) : null}
                  onLaborChange={(v) => setLaborBySystem((prev) => ({ ...prev, [col.system.id]: v }))}
                  onOverheadChange={(v) => setOverheadBySystem((prev) => ({ ...prev, [col.system.id]: v }))}
                  onShippingChange={(v) => setShippingPctOv(String(v))}
                  onHandlingChange={(v) => setHandlingPctOv(String(v))}
                  onProfitChange={(v) => setProfitBySystem((prev) => ({ ...prev, [col.system.id]: v }))}
                  onNdlChange={(v) => setNdlBySystem((prev) => ({ ...prev, [col.system.id]: v }))}
                  onHailRiderChange={(v) => setHailRiderBySystem((prev) => ({ ...prev, [col.system.id]: v }))}
                  savingToDeal={savingToDeal}
                  testIdSuffix={idx}
                  unsavedFields={unsavedFields}
                />
                );
              })}
            </div>
            {/* Bulk actions row — visible only when we have a deal + columns. */}
            {!deal && columns.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-3 p-3 border-2 border-amber-400 bg-amber-50 rounded-sm" data-testid="no-deal-hint">
                <span className="text-xs font-bold text-amber-900 uppercase tracking-wider">Pick a project to unlock the Set Option / finalize buttons:</span>
                <select
                  value={selectedDealId}
                  onChange={(e) => setSelectedDealId(e.target.value)}
                  data-testid="select-deal-inline"
                  className="border border-amber-500 px-3 h-9 text-sm bg-white focus:outline-none focus:border-blue-700 max-w-[340px]"
                >
                  <option value="">— No deal (comparison only) —</option>
                  {deals.map((d) => {
                    const label = `${d.title || d.name || "(untitled)"}${d.stage ? ` · ${d.stage}` : ""}`;
                    return <option key={d.id} value={d.id}>{label.slice(0, 80)}</option>;
                  })}
                </select>
              </div>
            )}
            {deal && columns.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                {mode === "estimate" ? (
                  <button
                    onClick={setAllOptionsOnDeal}
                    disabled={savingToDeal}
                    className="inline-flex items-center gap-1.5 px-4 h-9 text-[11px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 rounded-sm"
                    data-testid="bulk-set-all-options"
                  >
                    <Save className="w-3.5 h-3.5" /> Set ALL → Options on Deal
                  </button>
                ) : (
                  <button
                    onClick={downloadPoOnly}
                    disabled={generatingPo}
                    className="inline-flex items-center gap-1.5 px-4 h-9 text-[11px] font-bold uppercase tracking-wider border border-blue-700 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50 rounded-sm"
                    data-testid="download-po-existing"
                    title="Generate a PO PDF from the deal's existing material take-off (for the picked manufacturer)."
                  >
                    <FileText className="w-3.5 h-3.5" /> {generatingPo ? "Generating…" : `PO PDF — ${selectedVendor}`}
                  </button>
                )}
                {/* Return to Deal — one-tap jump back to the deal page. Keeps
                    the rep in a single-place workflow (matches the pipeline
                    jump-to-section pattern on DealDetail). */}
                <button
                  onClick={() => nav(`/projects/${deal.id}`)}
                  className="inline-flex items-center gap-1.5 px-4 h-9 text-[11px] font-bold uppercase tracking-wider border border-zinc-950 bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm"
                  data-testid="return-to-deal-btn"
                  title={`Back to ${deal.title || "the deal page"}`}
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Return to Deal
                </button>
              </div>
            )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function CompareColumn({ col, settings, totalSf, mode, onRemove, onSetOption, onPushMaterials, onPushAndPo, savingToDeal, testIdSuffix, onLaborChange, onOverheadChange, onProfitChange, onNdlChange, onHailRiderChange, onShippingChange, onHandlingChange, unsavedFields = [], crossVendor = false }) {
  const { system, lines, addonLines, rawCost, markedUp, handling, warrantyAdd, isEverest, isGaf, ndlAvailable, isNdl, laborAdd, overhead, profit, ohPct, prPct, customer, pricePerSf, customAddonTotal, hailRiderEligible, hailRiderAmount, hailRiderDefault } = col;
  const hasRecipe = lines.length > 0;
  const optionLetter = { 25: "A", 20: "B", 15: "C", 10: "D" }[system.warranty_years];
  return (
    <div className="bg-white border border-zinc-200 rounded-sm shadow-sm flex flex-col" data-testid={`compare-col-${testIdSuffix}`}>
      {unsavedFields.length > 0 && (
        <div
          className="px-3 py-2 bg-amber-100 border-b-2 border-amber-500 text-amber-900 text-[11px] font-semibold flex items-center gap-2"
          data-testid={`compare-col-unsaved-${testIdSuffix}`}
          title="Values in this column differ from what's saved on the deal. They live only in the Calculator UI until you commit them."
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600 animate-pulse" />
          <span className="uppercase tracking-widest text-[9px] font-bold">Unsaved</span>
          <span className="truncate">{unsavedFields.join(" · ")} — click <b>Push Materials</b> or <b>Set → Option {optionLetter}</b> to save to the deal.</span>
        </div>
      )}
      <div className="px-3 py-3 border-b-2 border-zinc-950 bg-zinc-50">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">{system.vendor}</div>
            {crossVendor && (
              <div className="inline-block px-1.5 py-0.5 mb-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 rounded-sm" data-testid={`cross-vendor-badge-${testIdSuffix}`}>
                Supplier price check
              </div>
            )}
            <div className="text-sm font-black truncate">{system.name}</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">
              {system.system_type} · {system.warranty_years}-yr warranty
            </div>
          </div>
          <button
            onClick={onRemove}
            className="p-1 hover:bg-red-100 text-red-700 rounded-sm flex-shrink-0"
            title="Remove from compare"
            data-testid={`remove-col-${testIdSuffix}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {!hasRecipe && (
        <div className="p-4 text-xs text-amber-700 bg-amber-50">
          No recipe assigned yet. Set ingredient products + coverage rates in <Link to="/catalog" className="underline">Product Catalog</Link>.
        </div>
      )}

      <div className="flex-1 p-3 space-y-3 text-xs">
        {lines.map((ln, i) => {
          const baseName = (ln.product.name || "").split(" — ")[0];
          // Strip the static "— N GPS" from recipe notes — the live coat
          // setup below is the source of truth for applied rates.
          const note = (ln.recipe_row?.notes || "").replace(/\s*[—-]\s*\d+(\.\d+)?\s*GPS.*$/i, "");
          return (
            <div key={i} className="pb-2 border-b border-zinc-100">
              <div className="font-bold truncate" title={baseName}>{baseName}</div>
              {note && (
                <div className="text-[10px] text-zinc-500 mt-0.5 italic truncate" title={note}>
                  {note}
                </div>
              )}
              {ln.coatCfg && (
                <div className="text-[10px] text-blue-700 mt-0.5 font-mono" data-testid={`coat-applied-${testIdSuffix}-${i}`}>
                  {ln.coatCfg.passes} pass{ln.coatCfg.passes > 1 ? "es" : ""} × {ln.coatCfg.gps} GPS = {Math.round(ln.coatCfg.passes * ln.coatCfg.gps * 100) / 100} GPS
                </div>
              )}
              <div className="text-[10px] text-zinc-500 mt-0.5">
                Needs {ln.qtyNeeded.toFixed(1)} {ln.product.unit}
              </div>
              <div className="mt-1 space-y-0.5">
                {ln.packed.map((pk, j) => {
                  const kind = classifyContainer(pk.product);
                  const kindLabel = kind === "tote" ? "tote" : kind === "drum" ? "drum" : kind === "pail" ? "pail" : (pk.product.unit || "");
                  return (
                    <div key={j} className="flex justify-between gap-2">
                      <span className="text-zinc-600 truncate">
                        {pk.qty} × {pk.product.package_size} {pk.product.unit} {kindLabel}
                      </span>
                      <span className="font-mono text-zinc-900">{formatCurrency(pk.cost)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-1 font-mono font-bold">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Line subtotal</span>
                <span>{formatCurrency(ln.lineCost)}</span>
              </div>
            </div>
          );
        })}

        {addonLines.length > 0 && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 pt-1">Add-ons</div>
            {addonLines.map((ln, i) => (
              <div key={`a-${i}`} className="pb-2 border-b border-zinc-100">
                <div className="font-bold truncate">{ln.addon.label}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{ln.qtyNeeded} {ln.addon.unit}</div>
                <div className="mt-1 space-y-0.5">
                  {ln.packed.map((pk, j) => {
                    const kind = classifyContainer(pk.product);
                    const kindLabel = kind === "tote" ? "tote" : kind === "drum" ? "drum" : kind === "pail" ? "pail" : (pk.product.unit || "");
                    return (
                      <div key={j} className="flex justify-between gap-2">
                        <span className="text-zinc-600 truncate">{pk.qty} × {pk.product.package_size} {pk.product.unit} {kindLabel}</span>
                        <span className="font-mono">{formatCurrency(pk.cost)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1 font-mono font-bold">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">Line subtotal</span>
                  <span>{formatCurrency(ln.lineCost)}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="border-t-2 border-zinc-950 p-3 bg-zinc-50 space-y-1 text-xs">
        <Row label="Raw materials" value={rawCost} />
        <PctEditableRow
          label="Shipping"
          pct={col.markupPct ?? settings.markup_pct}
          amount={markedUp - rawCost}
          onChange={onShippingChange}
          testId={`shipping-pct-${testIdSuffix}`}
        />
        <PctEditableRow
          label="Handling"
          pct={col.handlingPct ?? settings.handling_pct}
          amount={handling}
          onChange={onHandlingChange}
          testId={`handling-pct-${testIdSuffix}`}
        />
        {warrantyAdd > 0 && (
          <Row
            label={(isEverest || isGaf)
              ? `+ Warranty (${isNdl ? "NDL" : "Standard"}, ${system.warranty_years}-yr)`
              : `+ Warranty (${system.warranty_years}-yr)`}
            value={warrantyAdd}
          />
        )}
        {/* NDL toggle — Everest + GAF, for warranty bands where NDL is offered. */}
        {(isEverest || isGaf) && ndlAvailable && (
          <label
            className="flex items-center justify-between gap-2 text-[11px] cursor-pointer select-none"
            data-testid={`ndl-toggle-${testIdSuffix}`}
            title={isEverest
              ? "NDL = No-Dollar-Limit warranty. $3,000 third-party inspection + $0.06/SF (10-yr), $0.09/SF (15-yr) or $0.12/SF (20-yr). Standard warranty is $1,000 flat with no inspection."
              : "GAF NDL warranty: $0.05/SF (10-yr, min $1,000), $0.07/SF (15-yr, min $1,250), $0.10/SF (20-yr, min $1,500), $0.12/SF (25-yr, min $2,000) + $1,000 inspection. The Standard warranty is already in the base price — this upgrade adds only the difference."}
          >
            <span className="text-zinc-600">NDL warranty <span className="text-zinc-400 font-mono">{isEverest ? "($3,000 + per-SF)" : "(per-SF upgrade)"}</span></span>
            <input
              type="checkbox"
              checked={!!isNdl}
              onChange={(e) => onNdlChange?.(e.target.checked)}
              className="h-4 w-4"
            />
          </label>
        )}
        {/* NDL Upgrade + Hail Rider (bundled) — Western Colloid 20/25-yr.
            Customer accepts the NDL upgrade and gets Hail Rider bundled at
            no extra decision — ONE combined $ per tier on the scope.
            Default = max(SF×NDL rate, NDL min) + max(SF×Hail rate, Hail min),
            with SF rounded UP to the next 1,000 SF per SealTech ops rule.
              20-yr: NDL $0.15/SF (min $1,750) + Hail $0.04/SF (min $750)
              25-yr: NDL $0.20/SF (min $2,000) + Hail $0.05/SF (min $1,000)
            Rep can override or clear to omit the line. Not folded into
            the base customer price — the scope PDF shows it as a separate
            optional row so the customer opts in explicitly. */}
        {hailRiderEligible && (
          <div
            className="flex items-center justify-between gap-2 text-[11px]"
            data-testid={`hail-rider-row-${testIdSuffix}`}
            title="Optional NDL Warranty Upgrade (bundles Hail Rider on 20/25-yr). Default is the sum of NDL + Hail Rider at SealTech Feb-2026 pricing, with SF rounded up to the next 1,000 SF. Leave blank / 0 to omit."
          >
            <span className="text-zinc-600">
              NDL + Hail Rider <span className="text-zinc-400 font-mono">(opt.)</span>
            </span>
            <div className="flex items-center gap-1">
              <span className="text-zinc-400 text-[10px] font-mono">$</span>
              <input
                type="number"
                min="0"
                step="10"
                value={hailRiderAmount === 0 ? "" : hailRiderAmount}
                placeholder={Math.round(hailRiderDefault).toString()}
                onChange={(e) => onHailRiderChange?.(e.target.value === "" ? 0 : Number(e.target.value))}
                data-testid={`hail-rider-input-${testIdSuffix}`}
                className="w-24 border border-zinc-300 px-2 h-7 text-xs font-mono text-right focus:outline-none focus:border-blue-700"
              />
            </div>
          </div>
        )}
        {/* 1099 Subcontractor / Labor — per-warranty free-form $ */}
        <div className="flex items-center justify-between gap-2 font-mono">
          <label className="text-zinc-600 text-[11px]">+ 1099 Sub Labor ($ for this option)</label>
          <input
            type="number" min="0" step="50"
            value={laborAdd || ""}
            onChange={(e) => onLaborChange?.(Number(e.target.value) || 0)}
            placeholder="0"
            data-testid={`labor-input-${testIdSuffix}`}
            className="w-24 border border-zinc-300 px-2 h-7 text-xs font-mono text-right focus:outline-none focus:border-blue-700"
            title="Total 1099 subcontractor labor $ for this system on this job (per-SF × SF or flat-project). Saved to the deal's Subcontractor cost bucket on Set → Option — W2 Labor bucket stays reserved for future in-house employees."
          />
        </div>
        {(settings.overhead_pct || 0) > 0 && (
          <PctEditableRow
            label="Overhead"
            pct={ohPct}
            amount={overhead}
            onChange={onOverheadChange}
            testId={`overhead-pct-${testIdSuffix}`}
          />
        )}
        {(settings.profit_pct || 0) > 0 && (
          <PctEditableRow
            label="Profit"
            pct={prPct}
            amount={profit}
            onChange={onProfitChange}
            testId={`profit-pct-${testIdSuffix}`}
            step="0.1"
          />
        )}
        {/* Custom add-ons (rep-entered free-form rows) — show as a separate
            line so the rep can see exactly how much the WC add-ons bumped
            the customer total. Only renders when there's a non-zero amount. */}
        {(customAddonTotal || 0) > 0 && (
          <div className="flex items-baseline justify-between text-xs font-mono pt-1 text-zinc-700">
            <span className="text-zinc-600">+ Custom Add-Ons</span>
            <span data-testid={`custom-addon-total-${testIdSuffix}`}>{formatCurrency(customAddonTotal)}</span>
          </div>
        )}
        <div className="border-t border-zinc-300 pt-1.5 mt-1.5 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Customer Price</div>
            {totalSf > 0 && (
              <div className="text-[10px] text-zinc-500 font-mono">
                {pricePerSf.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 })}/SF
              </div>
            )}
          </div>
          <div className="text-lg font-black font-mono" data-testid={`customer-price-${testIdSuffix}`}>
            {formatCurrency(customer)}
          </div>
        </div>

        {/* Mode-specific action button(s) */}
        {mode === "estimate" && onSetOption && optionLetter && (
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <button
              onClick={onSetOption}
              disabled={savingToDeal || !hasRecipe}
              className="inline-flex items-center justify-center gap-1 px-2 h-9 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
              data-testid={`set-option-${testIdSuffix}`}
              title={`Writes ${formatCurrency(customer)} into Option ${optionLetter} on the deal (price only — no cost lines, no PO).`}
            >
              <Save className="w-3 h-3" />
              {savingToDeal ? "Saving…" : `Set → Option ${optionLetter}`}
            </button>
            {onPushMaterials && (
              <button
                onClick={onPushMaterials}
                disabled={savingToDeal || !hasRecipe}
                className="inline-flex items-center justify-center gap-1 px-2 h-9 text-[10px] font-bold uppercase tracking-wider bg-indigo-700 text-white hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
                data-testid={`push-materials-est-${testIdSuffix}`}
                title="Adds BoM lines to the deal's Material Take-Off AND Vendor Cost Line Items — ready for a Purchase Order."
              >
                <Package className="w-3 h-3" />
                {savingToDeal ? "Saving…" : "Push to Take-Off"}
              </button>
            )}
          </div>
        )}
        {mode === "estimate" && onSetOption && !optionLetter && (
          <div className="mt-3 space-y-2">
            <div className="px-2 py-2 bg-amber-50 border border-amber-200 text-[10px] text-amber-800 rounded-sm text-center">
              {system.warranty_years || "?"}-yr warranty has no Option slot — A/B/C/D map to 25/20/15/10-yr. The price is calculated for reference only.
            </div>
            {onPushMaterials && (
              <button
                onClick={onPushMaterials}
                disabled={savingToDeal || !hasRecipe}
                className="w-full inline-flex items-center justify-center gap-1 px-2 h-9 text-[10px] font-bold uppercase tracking-wider bg-indigo-700 text-white hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
                data-testid={`push-materials-est-alt-${testIdSuffix}`}
                title="Adds BoM lines to the deal's Material Take-Off AND Vendor Cost Line Items."
              >
                <Package className="w-3 h-3" />
                {savingToDeal ? "Saving…" : "Push to Take-Off"}
              </button>
            )}
          </div>
        )}
        {mode === "materials" && onPushMaterials && (
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <button
              onClick={onPushMaterials}
              disabled={savingToDeal || !hasRecipe}
              className="inline-flex items-center justify-center gap-1 px-2 h-9 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
              data-testid={`push-materials-${testIdSuffix}`}
              title="Adds BoM lines to the deal's Material Take-off AND Vendor Cost Line Items."
            >
              <Save className="w-3 h-3" />
              {savingToDeal ? "Saving…" : "Push Materials"}
            </button>
            <button
              onClick={onPushAndPo}
              disabled={savingToDeal || !hasRecipe}
              className="inline-flex items-center justify-center gap-1 px-2 h-9 text-[10px] font-bold uppercase tracking-wider bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm"
              data-testid={`push-and-po-${testIdSuffix}`}
              title="Push materials AND immediately download the Purchase Order PDF for this vendor."
            >
              <FileText className="w-3 h-3" />
              {savingToDeal ? "Saving…" : "Push + PO"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between font-mono">
      <span className="text-zinc-600">{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}

function PctEditableRow({ label, pct, amount, onChange, testId, step = "0.5" }) {
  return (
    <div className="flex items-center justify-between gap-2 font-mono">
      <span className="text-zinc-600 text-[11px] flex items-center gap-1">
        +
        <input
          type="number"
          min="0"
          max="100"
          step={step}
          value={pct ?? ""}
          onChange={(e) => onChange?.(e.target.value === "" ? null : Number(e.target.value))}
          className="w-12 border border-zinc-300 px-1 h-6 text-[11px] font-mono text-right focus:outline-none focus:border-blue-700"
          data-testid={testId}
          title={`${label} % — per-option override. Clear the field to fall back to the global default.`}
        />
        % {label}
      </span>
      <span>{formatCurrency(amount)}</span>
    </div>
  );
}
